/* global __firebase_config, __app_id, __initial_auth_token */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  onSnapshot,
  addDoc,
  deleteDoc,
  serverTimestamp,
  updateDoc,
  arrayUnion
} from 'firebase/firestore';
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  signInWithCustomToken
} from 'firebase/auth';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  LogOut, Printer, Flower, Search, 
  UserCog, X, Bell, Trash2, Plus, Pencil, Save, Users2, ListTodo,
  ChevronLeft, ChevronRight, Circle, CheckCircle2, 
  ClipboardList, CalendarDays, 
  AlertTriangle, KeyRound, UserPlus,
  ChevronUp, ChevronDown, FileText,
  Calendar as CalendarIcon
} from 'lucide-react';

// -------------------------------------------------------------------------
// 1. 환경 설정 및 데이터 정의
// -------------------------------------------------------------------------

const KR_HOLIDAYS = {
  '2025-01-01': '신정', '2025-01-28': '설날 연휴', '2025-01-29': '설날', '2025-01-30': '설날 연휴',
  '2025-03-01': '삼일절', '2025-03-03': '대체공휴일', '2025-05-05': '어린이날/부처님오신날', '2025-05-06': '대체공휴일',
  '2025-06-06': '현충일', '2025-08-15': '광복절', '2025-10-03': '개천절',
  '2025-10-05': '추석 연휴', '2025-10-06': '추석', '2025-10-07': '추석 연휴', '2025-10-08': '대체공휴일',
  '2025-10-09': '한글날', '2025-12-25': '성탄절'
};

const getSafeEnvVar = (key, fallback) => {
  try {
    if (typeof window !== 'undefined' && window[key]) return window[key];
    if (key === '__firebase_config' && typeof __firebase_config !== 'undefined') return __firebase_config;
    if (key === '__app_id' && typeof __app_id !== 'undefined') return __app_id;
    if (key === '__initial_auth_token' && typeof __initial_auth_token !== 'undefined') return __initial_auth_token;
  } catch (e) {}
  return fallback;
};

const rawConfig = getSafeEnvVar('__firebase_config', null);
const firebaseConfig = rawConfig ? JSON.parse(rawConfig) : {
  apiKey: "AIzaSyDZiPCGF77lbIaEC6W8GpsOs7n5MntEleQ",
  authDomain: "flower-office-work.firebaseapp.com",
  projectId: "flower-office-work",
  storageBucket: "flower-office-work.firebasestorage.app",
  messagingSenderId: "516440417618",
  appId: "1:516440417618:web:4faacc6e413d3bf9e3868b",
  measurementId: "G-36TY0W5RSM"
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const messaging = (() => { try { return getMessaging(firebaseApp); } catch(e) { return null; } })();
const functions = getFunctions(firebaseApp);

const appId = getSafeEnvVar('__app_id', 'flower-office-work-official');
const initialToken = getSafeEnvVar('__initial_auth_token', null);

const SCHEDULE_COLORS = [
  { id: 'pink', bg: 'bg-pink-100', border: 'border-pink-200', dot: 'bg-pink-500', hex: '#ec4899' },
  { id: 'blue', bg: 'bg-blue-100', border: 'border-blue-200', dot: 'bg-blue-500', hex: '#3b82f6' },
  { id: 'green', bg: 'bg-emerald-100', border: 'border-emerald-200', dot: 'bg-emerald-500', hex: '#10b981' },
  { id: 'yellow', bg: 'bg-amber-100', border: 'border-amber-200', dot: 'bg-amber-500', hex: '#f59e0b' },
  { id: 'slate', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-500', hex: '#64748b' }
];

const fmtLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const getToday = () => fmtLocalDate(new Date());
const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return fmtLocalDate(d);
};

const eachDate = (start, end) => {
  const out = [];
  const s = new Date(start + 'T00:00:00'), e = new Date(end + 'T00:00:00');
  for (let d = new Date(s); d <= e && out.length < 400; d.setDate(d.getDate() + 1)) out.push(fmtLocalDate(new Date(d)));
  return out;
};

const newRangeId = () => `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const getFormattedDate = (dateStr) => {
  if (!dateStr) return "";
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  const date = new Date(dateStr);
  return `${dateStr} (${days[date.getDay()]})`;
};

// --- Error Boundary ---
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-6 text-center font-bold">
          <AlertTriangle className="w-12 h-12 text-rose-500 mb-4" />
          <h2 className="text-lg font-black text-slate-800 tracking-tight">시스템 오류가 발생했습니다.</h2>
          <button 
            onClick={() => { localStorage.removeItem('flower_log_user_official'); window.location.reload(); }}
            className="mt-4 px-6 py-2 bg-slate-900 text-white rounded-xl font-black hover:bg-slate-800 transition text-xs"
          >
            새로고침 및 재로그인
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// -------------------------------------------------------------------------
// 2. 메인 앱 컴포넌트
// -------------------------------------------------------------------------
function AppContent() {
  const [user, setUser] = useState(null); 
  const [appUser, setAppUser] = useState(null); 
  const [logs, setLogs] = useState([]);
  const [notices, setNotices] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [schedules, setSchedules] = useState({}); 
  const [todos, setTodos] = useState([]);
  const [profiles, setProfiles] = useState({}); 
  const [loginError, setLoginError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  
  const [activeTab, setActiveTab] = useState('notice'); 
  const [message, setMessage] = useState({ text: '', type: '' });
  
  const [showUserManageModal, setShowUserManageModal] = useState(false);
  const [showMyProfileModal, setShowMyProfileModal] = useState(false);
  
  const [newUserData, setNewUserData] = useState({ id: '', name: '', pw: '', role: '선임', dept: '본사' });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [myNewPw, setMyNewPw] = useState('');

  const [ranks] = useState(['대표', '사장', '이사', '부장', '차장', '과장', '대리', '주임', '선임', '사원']);
  const [depts] = useState(['본사', '총괄지원부', '콜센터', '배송팀', '정산팀']);

  const [isWritingMeeting, setIsWritingMeeting] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [meetingData, setMeetingData] = useState({ date: getToday(), title: '', participants: '', content: '' });
  const [meetingFilterMember, setMeetingFilterMember] = useState('all'); 
  const [appliedMeetingRange, setAppliedMeetingRange] = useState({ start: getToday().substring(0, 4) + '-01-01', end: getToday() });
  const [meetingRangeDraft, setMeetingRangeDraft] = useState({ start: getToday().substring(0, 4) + '-01-01', end: getToday() });
  const [meetingSearch, setMeetingSearch] = useState('');
  const [meetingSearchDraft, setMeetingSearchDraft] = useState('');

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [tempScheduleItems, setTempScheduleItems] = useState([]);
  const [removedRanges, setRemovedRanges] = useState([]);

  const [sharedSchedules, setSharedSchedules] = useState({});
  const [sharedCurrentDate, setSharedCurrentDate] = useState(new Date());
  const [showSharedScheduleModal, setShowSharedScheduleModal] = useState(false);
  const [selectedSharedDate, setSelectedSharedDate] = useState(null);
  const [tempSharedItems, setTempSharedItems] = useState([]);
  const [removedSharedRanges, setRemovedSharedRanges] = useState([]);

  const [phoneEduData, setPhoneEduData] = useState(null);
  const [showPhoneEduCellModal, setShowPhoneEduCellModal] = useState(false);
  const [selectedPhoneEduCell, setSelectedPhoneEduCell] = useState(null);
  const [phoneEduDateInput, setPhoneEduDateInput] = useState('');
  const [showAddColModal, setShowAddColModal] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [phoneEduMemoEdit, setPhoneEduMemoEdit] = useState('');
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [showColColorPicker, setShowColColorPicker] = useState(null);
  const [workStatusSubTab, setWorkStatusSubTab] = useState('phone-edu');
  const [logStatusMode, setLogStatusMode] = useState('week');
  const [logStatusRange, setLogStatusRange] = useState({ start: getToday().substring(0,8) + '01', end: getToday() });

  const [showAiSummaryModal, setShowAiSummaryModal] = useState(false);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryResult, setAiSummaryResult] = useState(null);
  const [aiSummaryError, setAiSummaryError] = useState('');
  const [aiSummaryMeta, setAiSummaryMeta] = useState({ startDate: '', endDate: '' });
  const [aiHistory, setAiHistory] = useState([]);
  const [showAiConfirm, setShowAiConfirm] = useState(false);
  const [aiGuideline, setAiGuideline] = useState('');
  const [showGuidelineModal, setShowGuidelineModal] = useState(false);
  const [guidelineDraft, setGuidelineDraft] = useState('');
  const [aiHistoryFilter, setAiHistoryFilter] = useState('all');

  const [meetingAiLoading, setMeetingAiLoading] = useState(false);
  const [meetingAiResult, setMeetingAiResult] = useState('');
  const [meetingAiError, setMeetingAiError] = useState('');
  const [showMeetingAiModal, setShowMeetingAiModal] = useState(false);
  const [showMeetingRaw, setShowMeetingRaw] = useState(false);

  const [secEduData, setSecEduData] = useState(null);
  const [showSecEduCellModal, setShowSecEduCellModal] = useState(false);
  const [selectedSecEduCell, setSelectedSecEduCell] = useState(null);
  const [secEduDateInput, setSecEduDateInput] = useState('');
  const [showAddSecEduEmployeeModal, setShowAddSecEduEmployeeModal] = useState(false);
  const [newSecEduEmployee, setNewSecEduEmployee] = useState({ name: '', company: '' });
  const [secEduYear, setSecEduYear] = useState(new Date().getFullYear());
  const secEduTableRef = useRef(null);

  const [todoInput, setTodoInput] = useState('');
  const [todoDate, setTodoDate] = useState(getToday());
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [editingTodoText, setEditingTodoText] = useState('');

  const [logDate, setLogDate] = useState(getToday());
  const [content, setContent] = useState('✔️ ');
  const [appliedStartDate, setAppliedStartDate] = useState(getToday());
  const [appliedEndDate, setAppliedEndDate] = useState(getToday());
  const [filterMember, setFilterMember] = useState('all');
  const [activeRangeMode, setActiveRangeMode] = useState('오늘'); 

  const textareaRef = useRef(null);
  const logsRef = useRef([]);

  const showMsg = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  }, []);

  const RANK_PRIORITY = useMemo(() => {
    const priority = {};
    ranks.forEach((r, i) => priority[r] = i);
    return priority;
  }, [ranks]);

  const isSystemAdmin = useMemo(() => {
    if (!appUser) return false;
    return ['admin', 'user1', 'user2'].includes(appUser.id) || ['대표', '사장', '이사'].includes(appUser.role);
  }, [appUser]);

  // AI 요약·기록 접근권 (최고관리자 + 추가 허용 계정)
  const AI_EXTRA_IDS = ['iiillljjj']; // 신성은
  const canUseAi = useMemo(() => {
    if (!appUser) return false;
    return isSystemAdmin || AI_EXTRA_IDS.includes(appUser.id);
  }, [appUser, isSystemAdmin]);

  const canSeeDataOf = useCallback((targetUserId, dataStatus = 'submitted') => {
    if (!appUser) return false;
    if (dataStatus === 'saved' || dataStatus === 'Draft') return targetUserId === appUser.id;
    if (targetUserId === appUser.id) return true;
    if (isSystemAdmin) return true;
    
    const myDept = appUser.dept || '';
    const myRank = appUser.role;
    const targetProfile = profiles[targetUserId];
    if (!targetProfile) return false;
    
    const targetRank = targetProfile.role || '사원';
    const targetDept = targetProfile.dept || '기타';
    const myPriority = RANK_PRIORITY[myRank] ?? 99;
    const targetPriority = RANK_PRIORITY[targetRank] ?? 99;
    
    if (myDept === '본사') return true;
    if (myDept === '총괄지원부') return targetDept !== '본사' ? targetPriority > myPriority : false;
    if (myRank === '대리') return (myDept === targetDept) && (targetPriority > myPriority);
    return false;
  }, [appUser, profiles, isSystemAdmin, RANK_PRIORITY]);

  const activeUserList = useMemo(() => {
    return Object.entries(profiles)
      .filter(([, u]) => u.visible !== false)
      .map(([id, u]) => ({ id, ...u }))
      .sort((a, b) => {
        const pA = RANK_PRIORITY[a.role] ?? 99;
        const pB = RANK_PRIORITY[b.role] ?? 99;
        if (pA !== pB) return pA - pB;
        return a.name.localeCompare(b.name, 'ko');
      });
  }, [profiles, RANK_PRIORITY]);

  const setRangeMode = useCallback((type) => {
    setActiveRangeMode(type); 
    const now = new Date();
    if (type === '어제') {
      const y = getYesterday(); setAppliedStartDate(y); setAppliedEndDate(y);
    } else if (type === '오늘') {
      const t = getToday(); setAppliedStartDate(t); setAppliedEndDate(t);
    } else if (type === '이번주') {
      const day = now.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const mon = new Date(now); mon.setDate(now.getDate() + diff);
      setAppliedStartDate(fmtLocalDate(mon)); setAppliedEndDate(getToday());
    } else if (type === '지난주') {
      const day = now.getDay();
      const diffToMon = day === 0 ? -6 : 1 - day;
      const lastMon = new Date(now); lastMon.setDate(now.getDate() + diffToMon - 7);
      const lastSun = new Date(now); lastSun.setDate(now.getDate() + diffToMon - 1);
      setAppliedStartDate(fmtLocalDate(lastMon));
      setAppliedEndDate(fmtLocalDate(lastSun));
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    const id = e.target.loginId.value.trim();
    const pw = e.target.loginPw.value.trim();
    
    if (!id || !pw) {
      setLoginError('아이디와 비밀번호를 모두 입력해주세요.');
      return;
    }

    if (!user) {
        setLoginError('인증 대기 중입니다. 잠시 후 다시 시도해 주세요.');
        signInAnonymously(auth).catch(err => console.error(err));
        return;
    }

    try {
      const profileRef = doc(db, 'artifacts', appId, 'public', 'data', 'user_profiles', id);
      const snap = await getDoc(profileRef);
      
      if (snap.exists()) {
        const data = snap.data();
        if (data.pw === pw) {
          const u = { id, ...data };
          setAppUser(u);
          localStorage.setItem('flower_log_user_official', JSON.stringify(u));
          showMsg(`${u.name}님, 환영합니다!`);
          if (['admin', 'user1', 'user2'].includes(id) || ['대표', '사장', '이사'].includes(data.role)) {
            registerFcmToken(id);
          }
        } else {
          setLoginError('비밀번호가 일치하지 않습니다.');
        }
      } else {
        setLoginError('존재하지 않는 사용자 아이디입니다.');
      }
    } catch (err) {
      console.error("Login Error:", err);
      if (err.code === 'permission-denied') {
        setLoginError('보안 규칙이 만료되었습니다. Firebase에서 규칙을 업데이트하세요.');
      } else {
        setLoginError(`서버 연결 실패: ${err.code || '인터넷 상태 확인 필요'}`);
      }
    }
  };

  const saveWorkLog = async (txt, isFinal = false, targetDate = logDate, dayOff = false) => {
    if (!appUser || !user) return;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'work_logs', `${appUser.id}_${targetDate}`), {
        userId: appUser.id, userName: appUser.name, date: targetDate, content: txt,
        submitted: isFinal, dayOff: dayOff, updatedAt: new Date().toISOString()
      }, { merge: true });
      if (dayOff) showMsg('휴무로 제출되었습니다.');
      else if (isFinal) showMsg('업무 일지가 제출되었습니다.');
      else showMsg('임시 저장되었습니다.');
    } catch (err) {
        console.error(err);
        showMsg('저장 실패', 'error');
    }
  };

  const submitDayOff = async () => {
    if (!window.confirm(`${logDate} 을(를) 휴무로 제출하시겠습니까?`)) return;
    await saveWorkLog('휴무', true, logDate, true);
  };

  const handleAiSummary = async () => {
    setAiSummaryMeta({ startDate: appliedStartDate, endDate: appliedEndDate });
    setShowAiSummaryModal(true); setAiSummaryLoading(true); setAiSummaryResult(null); setAiSummaryError('');
    try {
      const fn = httpsCallable(functions, 'summarizeWorkLogs');
      const res = await fn({ requesterId: appUser.id, userId: filterMember, startDate: appliedStartDate, endDate: appliedEndDate });
      setAiSummaryResult(res.data);
    } catch (e) {
      setAiSummaryError(e.message || 'AI 요약에 실패했습니다.');
    } finally {
      setAiSummaryLoading(false);
    }
  };

  const handleOrganizeMeeting = async () => {
    if (!meetingData.content || !meetingData.content.trim()) { showMsg('정리할 회의 내용을 먼저 입력하세요.', 'error'); return; }
    setShowMeetingAiModal(true); setMeetingAiLoading(true); setMeetingAiResult(''); setMeetingAiError('');
    try {
      const fn = httpsCallable(functions, 'organizeMeetingNotes');
      const res = await fn({ requesterId: appUser.id, content: meetingData.content });
      setMeetingAiResult((res.data && res.data.organized) || '');
    } catch (e) {
      setMeetingAiError(e.message || 'AI 정리에 실패했습니다.');
    } finally {
      setMeetingAiLoading(false);
    }
  };

  const applyMeetingOrganize = () => {
    const organized = cleanSummary(meetingAiResult).trim();
    if (!organized) return;
    setMeetingData((d) => ({ ...d, rawContent: d.rawContent || d.content, content: organized }));
    setShowMeetingAiModal(false);
    showMsg('AI 정리본을 적용했습니다. 내용을 확인·수정 후 저장하세요.');
  };

  const cleanSummary = (s) => (s || '')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*/g, '')
    .replace(/__/g, '')
    .replace(/`/g, '')
    .replace(/^\s*[*]\s+/gm, '- ');

  // 회의록 정리본을 줄 단위 토큰으로 파싱 (화면/출력 공통)
  const parseMeetingLines = (text) => {
    let sec = '';
    return cleanSummary(text).split('\n').map((line) => {
      const t = line.trim();
      if (!t) return { kind: 'blank' };
      if (t.startsWith('【')) { sec = t; return { kind: 'section', text: t }; }
      const inSpeakerSec = sec.includes('전달·지시') || sec.includes('건의·문의');
      if (inSpeakerSec && !/^[-·•\d]/.test(t) && !/[:：]/.test(t) && t.length <= 40 && !/[.。!?…]$/.test(t)) {
        return { kind: 'speaker', text: t };
      }
      if (sec.includes('건의·문의')) {
        const m = t.match(/^([-·•]\s*)([^:：]{1,20})[:：](.*)$/);
        if (m) return { kind: 'namedBullet', lead: m[1], name: m[2].trim(), rest: m[3] };
      }
      return { kind: 'line', text: line };
    });
  };

  const renderMeetingBody = (text) => parseMeetingLines(text).map((p, i) => {
    if (p.kind === 'blank') return <div key={i} className="h-1.5"></div>;
    if (p.kind === 'section') return <div key={i} className="font-black text-pink-600 text-[17px] mt-5 pt-4 border-t border-slate-300 pb-1.5 first:mt-0 first:pt-0 first:border-t-0">{p.text}</div>;
    if (p.kind === 'speaker') return <div key={i} className="font-black text-slate-900 text-[15px] mt-3.5 pb-0.5 whitespace-pre-wrap">{p.text}</div>;
    if (p.kind === 'namedBullet') return <div key={i} className="whitespace-pre-wrap"><span className="font-black text-slate-900">{p.name}</span>:{p.rest}</div>;
    return <div key={i} className="whitespace-pre-wrap">{p.text}</div>;
  });

  const buildAiSummaryHtml = () => {
    if (!aiSummaryResult) return { styles: '', inner: '' };
    const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const styles = `body{font-family:'Pretendard',sans-serif;padding:14mm;color:#1e293b;line-height:1.7;} h1{font-size:22px;font-weight:900;margin:0 0 4px;} .meta{font-size:12px;color:#64748b;font-weight:700;margin-bottom:18px;border-bottom:2px solid #e2e8f0;padding-bottom:12px;} .sec{font-size:14px;font-weight:900;color:#db2777;margin:16px 0 4px;page-break-inside:avoid;break-inside:avoid;} .ln{font-size:13px;margin:2px 0;page-break-inside:avoid;break-inside:avoid;} .foot{margin-top:24px;font-size:10px;color:#94a3b8;}`;
    let sec = '';
    const lines = cleanSummary(aiSummaryResult.summary).split('\n').map((l) => {
      const t = l.trim();
      if (!t) return '<div style="height:6px"></div>';
      if (t.startsWith('【')) { sec = t; return `<div class="sec">${esc(t)}</div>`; }
      if (sec.includes('주요 업무 영역')) {
        const m = t.match(/^([-·•]\s*)?([^:：(]{1,20})[:：]\s*(.+)$/);
        if (m) return `<div class="ln">${m[1] ? '· ' : ''}<b>${esc(m[2].trim())}</b> : ${esc(m[3])}</div>`;
      }
      if (sec.includes('주요 성과') && !/^[-·•]/.test(t) && !/[:：]/.test(t) && t.length <= 16 && !/[.。!?…]$/.test(t)) {
        return `<div class="ln" style="margin-top:8px"><b>${esc(t)}</b></div>`;
      }
      return `<div class="ln">${esc(l)}</div>`;
    }).join('');
    const inner = `<h1>AI 업무일지 요약</h1><div class="meta">${esc(aiSummaryResult.userName)} · ${aiSummaryMeta.startDate} ~ ${aiSummaryMeta.endDate} · 업무일지 ${aiSummaryResult.count}건 분석</div>${lines}<div class="foot">※ AI가 생성한 참고용 요약입니다. 원문과 대조하여 확인하세요.</div>`;
    return { styles, inner };
  };

  const saveAiGuideline = async () => {
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'ai_config', 'main'), { guideline: guidelineDraft, updatedAt: new Date().toISOString() }, { merge: true });
      setShowGuidelineModal(false);
      showMsg('AI 분석 기준이 저장되었습니다.');
    } catch (e) { showMsg('저장 실패', 'error'); }
  };

  const deleteAiHistory = async (id) => {
    if (!window.confirm('이 AI 요약 기록을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'ai_summaries', id));
      showMsg('요약 기록이 삭제되었습니다.');
    } catch (e) { showMsg('삭제 실패', 'error'); }
  };

  const openSavedSummary = (rec) => {
    setAiSummaryResult({ userName: rec.userName, summary: rec.summary, count: rec.count });
    setAiSummaryMeta({ startDate: rec.startDate, endDate: rec.endDate });
    setAiSummaryError(''); setAiSummaryLoading(false); setShowAiSummaryModal(true);
  };

  const printAiSummary = () => {
    const { styles, inner } = buildAiSummaryHtml();
    const w = window.open('', '_blank');
    if (!w) return showMsg('팝업 차단을 해제해주세요.', 'error');
    const printExtra = '@page{size:A4 portrait;margin:24mm 16mm;} body{padding:0;}';
    w.document.write(`<html><head><title>AI 요약 - ${aiSummaryResult?.userName || ''}</title><style>@import url('https://fonts.googleapis.com/css2?family=Pretendard:wght@400;700;900&display=swap');${styles}${printExtra}</style></head><body>${inner}<script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };

  const wordAiSummary = () => {
    if (!aiSummaryResult) return;
    const { styles, inner } = buildAiSummaryHtml();
    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><style>${styles}</style></head><body>${inner}</body></html>`;
    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `AI요약_${(aiSummaryResult.userName || '무제').replace(/[\\/:*?"<>|]/g,'')}_${aiSummaryMeta.startDate}~${aiSummaryMeta.endDate}.doc`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    showMsg('Word 파일이 저장되었습니다.');
  };

  const pdfAiSummary = () => {
    if (!window.html2pdf) return showMsg('PDF 모듈을 불러오는 중입니다. 잠시 후 다시 시도하세요.', 'error');
    const { styles, inner } = buildAiSummaryHtml();
    const el = document.createElement('div');
    el.innerHTML = `<style>${styles}</style>${inner}`;
    el.style.width = '180mm';
    window.html2pdf().set({
      margin: [22, 14, 20, 14],
      filename: `AI요약_${(aiSummaryResult?.userName || '무제')}_${aiSummaryMeta.startDate}~${aiSummaryMeta.endDate}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    }).from(el).save();
    showMsg('PDF 파일을 저장합니다.');
  };

  const handleWorkLogKeyDown = (e) => {
    if (e.key === 'Enter') {
      const s = e.target.selectionStart;
      const val = e.target.value;
      let newVal = "";
      if (e.shiftKey) {
        newVal = val.substring(0, s) + "\n    " + val.substring(e.target.selectionEnd);
        setContent(newVal);
        setTimeout(() => { if(textareaRef.current) { textareaRef.current.selectionStart = textareaRef.current.selectionEnd = s + 5; } }, 0);
      } else {
        newVal = val.substring(0, s) + "\n✔️ " + val.substring(e.target.selectionEnd);
        setContent(newVal);
        setTimeout(() => { if(textareaRef.current) { textareaRef.current.selectionStart = textareaRef.current.selectionEnd = s + 4; } }, 0);
      }
      e.preventDefault();
    }
  };

  const handleSaveMeeting = useCallback(async (status = 'saved') => {
    if (!meetingData.title.trim()) return showMsg('주제를 입력해주세요.', 'error');
    if (!appUser || !user) return;
    try {
      const meetingId = meetingData.id || `meet_${appUser.id}_${Date.now()}`;
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'meetings', meetingId), {
        ...meetingData, id: meetingId, userId: appUser.id, authorName: appUser.name, 
        status: status, 
        submittedAt: status === 'submitted' ? new Date().toISOString() : (meetingData.submittedAt || null),
        lastEditedAt: new Date().toISOString(),
        createdAt: meetingData.createdAt || serverTimestamp()
      }, { merge: true });
      if (status === 'submitted') { 
        setIsWritingMeeting(false); 
        setMeetingData({date: getToday(), title: '', participants: '', content: ''}); 
        showMsg('회의록이 제출되었습니다.'); 
      } else showMsg('임시저장되었습니다.');
    } catch (err) { showMsg('저장 실패', 'error'); }
  }, [appUser, user, meetingData, appId, showMsg]);

  const buildMeetingHtml = useCallback((m) => {
    const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const authorRank = profiles[m.userId]?.role || '사원';
    const styles = `@page { margin: 24mm 12mm; } * { box-sizing: border-box; } body { font-family: 'Pretendard', sans-serif; padding: 4mm; color: #1e293b; } h1 { font-size: 28px; font-weight: 900; text-align: center; letter-spacing: 0.3em; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 15px; } table { width: 100%; border-collapse: collapse; margin-bottom: 20px; page-break-inside: avoid; } th, td { border: 1.5px solid #000; padding: 10px; font-size: 13px; } th { background: #f8fafc; font-weight: 900; width: 100px; } .content { width: 100%; padding: 6px 2px; } .m-sec { font-size: 17px; font-weight: 900; color: #db2777; margin: 18px 0 8px; padding-top: 14px; border-top: 1px solid #94a3b8; page-break-inside: avoid; page-break-after: avoid; break-inside: avoid; } .m-sec.first { margin-top: 0; padding-top: 0; border-top: none; } .m-spk { font-size: 15px; font-weight: 900; color: #0f172a; margin: 13px 0 4px; page-break-inside: avoid; page-break-after: avoid; break-inside: avoid; } .m-ln { font-size: 13px; line-height: 1.75; margin: 3px 0; page-break-inside: avoid; break-inside: avoid; } .foot { margin-top: 28px; text-align: right; font-weight: 900; font-size: 12px; }`;
    let first = true;
    const body = parseMeetingLines(m.content).map((p) => {
      if (p.kind === 'blank') return '<div style="height:7px"></div>';
      if (p.kind === 'section') { const cls = first ? 'm-sec first' : 'm-sec'; first = false; return `<div class="${cls}">${esc(p.text)}</div>`; }
      if (p.kind === 'speaker') return `<div class="m-spk">${esc(p.text)}</div>`;
      if (p.kind === 'namedBullet') return `<div class="m-ln"><b>${esc(p.name)}</b>:${esc(p.rest)}</div>`;
      return `<div class="m-ln">${esc(p.text)}</div>`;
    }).join('');
    const inner = `<h1>회 의 록</h1><table><tr><th>안 건</th><td colspan="3" style="font-weight:900;">${esc(m.title)}</td></tr><tr><th>일 시</th><td style="width:30%;">${esc(m.date)}</td><th>참석자</th><td>${esc(m.participants)}</td></tr><tr><th>작성자</th><td colspan="3">${esc(m.authorName)} (${esc(authorRank)})</td></tr></table><div class="content">${body}</div><div class="foot">FLOWER OFFICE</div>`;
    return { styles, inner };
  }, [profiles, parseMeetingLines]);

  const handlePrintMeeting = useCallback((m) => {
    const w = window.open('', '_blank');
    if (!w) return showMsg('팝업 차단을 해제해주세요.', 'error');
    const { styles, inner } = buildMeetingHtml(m);
    w.document.write(`<html><head><title>${m.title}</title><style>@import url('https://fonts.googleapis.com/css2?family=Pretendard:wght@400;700;900&display=swap'); ${styles}</style></head><body>${inner}<script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  }, [buildMeetingHtml, showMsg]);

  const handleMeetingWord = useCallback((m) => {
    const { styles, inner } = buildMeetingHtml(m);
    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><style>${styles}</style></head><body>${inner}</body></html>`;
    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `회의록_${(m.title || '무제').replace(/[\\/:*?"<>|]/g,'')}_${m.date}.doc`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    showMsg('Word 파일이 저장되었습니다.');
  }, [buildMeetingHtml, showMsg]);

  const handleMeetingPdf = useCallback((m) => {
    if (!window.html2pdf) return showMsg('PDF 모듈을 불러오는 중입니다. 잠시 후 다시 시도하세요.', 'error');
    const { styles, inner } = buildMeetingHtml(m);
    const el = document.createElement('div');
    el.innerHTML = `<style>${styles}</style>${inner}`;
    el.style.width = '180mm';
    window.html2pdf().set({
      margin: [26, 14, 26, 14],
      filename: `회의록_${(m.title || '무제').replace(/[\\/:*?"<>|]/g,'')}_${m.date}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], avoid: ['.m-sec', '.m-spk', '.m-ln'] }
    }).from(el).save();
    showMsg('PDF 파일을 저장합니다.');
  }, [buildMeetingHtml, showMsg]);

  const navItems = useMemo(() => {
    const all = [
      { id: 'notice', label: '공지' },
      { id: 'shared-schedule', label: '공유일정' },
      { id: 'schedule', label: '일정' },
      { id: 'todo', label: '할일' },
      { id: 'write', label: '작성' },
      { id: 'view', label: '조회' },
      { id: 'meeting', label: '회의록' },
      { id: 'work-status', label: '업무현황' }
    ];

    const aiAccess = ['admin', 'user1', 'user2', ...AI_EXTRA_IDS].includes(appUser?.id) || ['대표', '사장', '이사'].includes(appUser?.role);

    if (appUser?.id === 'admin') {
      const base = all.filter(item => ['notice', 'shared-schedule', 'view', 'meeting', 'work-status'].includes(item.id));
      return [...base, { id: 'ai-history', label: 'AI기록' }];
    }
    return aiAccess ? [...all, { id: 'ai-history', label: 'AI기록' }] : all;
  }, [appUser]);

  const navIconFor = (id) => id === 'notice' ? Bell : id === 'shared-schedule' ? Users2 : id === 'schedule' ? CalendarDays : id === 'todo' ? ListTodo : id === 'write' ? Pencil : id === 'view' ? Search : id === 'work-status' ? ClipboardList : id === 'ai-history' ? Flower : Users2;

  const koreanToday = (() => {
    const d = new Date();
    const days = ['일','월','화','수','목','금','토'];
    return `${d.getFullYear()}년 ${d.getMonth()+1}월 ${d.getDate()}일 (${days[d.getDay()]})`;
  })();

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const handleSaveSchedule = async () => {
    if (!appUser || !user || !selectedDate) return;
    const ref = (d) => doc(db, 'artifacts', appId, 'public', 'data', 'schedules', `${appUser.id}_${d}`);
    try {
      // 삭제된 기간 일정: 해당 기간 전체에서 제거
      for (const r of removedRanges) {
        for (const d of eachDate(r.start, r.end)) {
          const items = (schedules[d] || []).filter(x => x.rangeId !== r.id);
          await setDoc(ref(d), { userId: appUser.id, date: d, items });
        }
      }
      // 현재 날짜 항목 정리 (기간 항목엔 rangeId 부여)
      const items = tempScheduleItems.map(it => {
        if (it.endDate && it.endDate > selectedDate) {
          return { ...it, rangeId: it.rangeId || newRangeId(), rangeStart: selectedDate, rangeEnd: it.endDate };
        }
        const { endDate, rangeId, rangeStart, rangeEnd, ...rest } = it;
        return rest;
      });
      await setDoc(ref(selectedDate), { userId: appUser.id, date: selectedDate, items });
      // 기간 항목을 이후 날짜에 반영
      for (const it of items.filter(x => x.rangeId && x.rangeEnd)) {
        for (const d of eachDate(selectedDate, it.rangeEnd).slice(1)) {
          const cur = (schedules[d] || []).filter(x => x.rangeId !== it.rangeId);
          await setDoc(ref(d), { userId: appUser.id, date: d, items: [...cur, it] });
        }
      }
      showMsg('일정이 저장되었습니다.');
      setRemovedRanges([]);
      setShowScheduleModal(false);
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleSaveSharedSchedule = async () => {
    if (!appUser || !user || !selectedSharedDate) return;
    const ref = (d) => doc(db, 'artifacts', appId, 'public', 'data', 'shared_schedules', d);
    try {
      for (const r of removedSharedRanges) {
        for (const d of eachDate(r.start, r.end)) {
          const items = (sharedSchedules[d] || []).filter(x => x.rangeId !== r.id);
          await setDoc(ref(d), { date: d, items });
        }
      }
      const items = tempSharedItems.map(it => {
        if (it.endDate && it.endDate > selectedSharedDate) {
          return { ...it, rangeId: it.rangeId || newRangeId(), rangeStart: selectedSharedDate, rangeEnd: it.endDate };
        }
        const { endDate, rangeId, rangeStart, rangeEnd, ...rest } = it;
        return rest;
      });
      await setDoc(ref(selectedSharedDate), { date: selectedSharedDate, items });
      for (const it of items.filter(x => x.rangeId && x.rangeEnd)) {
        for (const d of eachDate(selectedSharedDate, it.rangeEnd).slice(1)) {
          const cur = (sharedSchedules[d] || []).filter(x => x.rangeId !== it.rangeId);
          await setDoc(ref(d), { date: d, items: [...cur, it] });
        }
      }
      showMsg('공유 일정이 저장되었습니다.');
      setRemovedSharedRanges([]);
      setShowSharedScheduleModal(false);
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleToggleTodo = async (todo) => {
    if (!appUser || !user) return;
    const nextCompleted = !todo.completed;
    
    // 완료 시 리스트 최하단으로 이동시키기 위해 최대 order값 계산
    let nextOrder = todo.order ?? 0;
    if (nextCompleted) {
      const maxOrder = todos.reduce((max, t) => Math.max(max, t.order ?? 0), 0);
      nextOrder = maxOrder + 1;
    }

    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'todos', todo.id), {
        completed: nextCompleted,
        order: nextOrder
      });
      
      if (nextCompleted) {
        const today = getToday();
        const logRef = doc(db, 'artifacts', appId, 'public', 'data', 'work_logs', `${appUser.id}_${today}`);
        const logSnap = await getDoc(logRef);
        
        let newContent = `✔️ ${todo.text}`;
        if (logSnap.exists()) {
          const currentLog = logSnap.data().content || "";
          const prefix = (currentLog.trim() === '✔️' || !currentLog.trim()) ? "" : "\n";
          newContent = currentLog.trim() + prefix + `✔️ ${todo.text}`;
        }
        
        await setDoc(logRef, {
          userId: appUser.id,
          userName: appUser.name,
          date: today,
          content: newContent,
          submitted: false,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        
        showMsg('할일 완료! 오늘 일지에 추가되었습니다.');
      }
    } catch (err) { showMsg('상태 변경 실패', 'error'); }
  };

  const handleAddTodo = async (e) => {
    e.preventDefault();
    if (!todoInput.trim() || !appUser || !user) return;
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'todos'), {
        userId: appUser.id, 
        text: todoInput, 
        date: todoDate, 
        completed: false, 
        order: todos.length,
        createdAt: serverTimestamp()
      });
      setTodoInput('');
      showMsg('할 일이 추가되었습니다.');
    } catch (err) { showMsg('추가 실패', 'error'); }
  };

  const moveTodo = async (index, direction) => {
    const sorted = [...todos].sort((a,b) => (a.order ?? 0) - (b.order ?? 0));
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;
    
    const item1 = sorted[index];
    const item2 = sorted[targetIndex];
    
    const order1 = item1.order ?? index;
    const order2 = item2.order ?? targetIndex;
    
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'todos', item1.id), { order: order2 });
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'todos', item2.id), { order: order1 });
    } catch (e) { showMsg('순서 변경 실패', 'error'); }
  };

  // FCM 토큰 등록 (관리자 전용)
  const registerFcmToken = useCallback(async (adminId) => {
    if (!messaging) return;
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const token = await getToken(messaging, {
        vapidKey: 'BJa5ca4polb4mEyRLxV8bbu-RIVQkbZUJ0bQqHe2plO0T7ym4fU1ZQER6cWsqBJ2EZGQiPwRj3l3EvmHMU_QC18'
      });
      if (token) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'user_profiles', adminId), {
          fcmToken: token,
          fcmTokens: arrayUnion(token)
        });
      }
    } catch (err) {
      console.warn('FCM 토큰 등록 실패:', err.message);
    }
  }, []);

  const enableNotifications = useCallback(async () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '');
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isIOS && !standalone) {
      showMsg('아이폰은 반드시 "홈 화면에 추가"한 앱 아이콘으로 열어야 알림을 켤 수 있습니다. (사파리 공유버튼 → 홈 화면에 추가)', 'error');
      return;
    }
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      showMsg('이 기기는 웹 알림을 지원하지 않습니다.', 'error');
      return;
    }
    if (!messaging) {
      showMsg('알림 모듈을 불러올 수 없습니다. (아이폰: iOS 16.4 이상 + 홈화면 추가 필요)', 'error');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        showMsg('알림 권한이 허용되지 않았습니다. 기기 설정 → 알림에서 이 앱을 허용해주세요.', 'error');
        return;
      }
      const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey: 'BJa5ca4polb4mEyRLxV8bbu-RIVQkbZUJ0bQqHe2plO0T7ym4fU1ZQER6cWsqBJ2EZGQiPwRj3l3EvmHMU_QC18',
        serviceWorkerRegistration: reg,
      });
      if (token && appUser) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'user_profiles', appUser.id), { fcmToken: token, fcmTokens: arrayUnion(token) });
        showMsg('알림이 켜졌습니다! 🔔');
      } else {
        showMsg('알림 토큰 발급에 실패했습니다. 잠시 후 다시 시도해주세요.', 'error');
      }
    } catch (err) {
      showMsg('알림 설정 실패: ' + (err.code || err.name || err.message || '알 수 없는 오류'), 'error');
    }
  }, [appUser, showMsg]);

  useEffect(() => {
    if (!messaging || !appUser) return;
    const unsubMsg = onMessage(messaging, (payload) => {
      const d = payload.data || payload.notification || {};
      if (d.title || d.body) showMsg(`🔔 ${d.body || d.title}`);
    });
    return () => unsubMsg();
  }, [appUser, showMsg]);

  // 이미 알림 권한을 허용한 관리자는 앱을 열 때마다 토큰 자동 갱신 (벨은 최초 1회만)
  useEffect(() => {
    if (!messaging || !appUser) return;
    const admin = ['admin', 'user1', 'user2'].includes(appUser.id) || ['대표', '사장', '이사'].includes(appUser.role);
    if (!admin) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (!('serviceWorker' in navigator)) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        await navigator.serviceWorker.ready;
        const token = await getToken(messaging, {
          vapidKey: 'BJa5ca4polb4mEyRLxV8bbu-RIVQkbZUJ0bQqHe2plO0T7ym4fU1ZQER6cWsqBJ2EZGQiPwRj3l3EvmHMU_QC18',
          serviceWorkerRegistration: reg,
        });
        if (token) {
          await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'user_profiles', appUser.id), { fcmToken: token, fcmTokens: arrayUnion(token) });
        }
      } catch (e) { /* 조용히 무시 */ }
    })();
  }, [appUser]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof initialToken !== 'undefined' && initialToken) {
          await signInWithCustomToken(auth, initialToken);
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("Auth Init Error:", err);
      } finally {
        setIsAuthenticating(false);
      }
    };
    initAuth();
    
    const saved = localStorage.getItem('flower_log_user_official');
    if (saved) {
      try { setAppUser(JSON.parse(saved)); } catch(e) { localStorage.removeItem('flower_log_user_official'); }
    }
    
    const unsubAuth = onAuthStateChanged(auth, (u) => { setUser(u); });
    return () => unsubAuth();
  }, [initialToken]);

  useEffect(() => {
    if (!user || !appUser) return;
    
    const unsubP = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'user_profiles'), (s) => {
      const m = {}; s.docs.forEach(d => m[d.id] = d.data()); setProfiles(m);
    }, (err) => console.error(err));

    const unsubL = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'work_logs'), (s) => {
      setLogs(s.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error(err));

    const unsubN = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'notices'), (s) => {
      setNotices(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    }, (err) => console.error(err));

    const unsubM = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'meetings'), (s) => {
      setMeetings(s.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error(err));

    const unsubS = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'schedules'), (s) => {
        const m = {}; s.docs.forEach(d => { if(d.data().userId === appUser?.id) m[d.data().date] = d.data().items; });
        setSchedules(m);
    }, (err) => console.error(err));

    const unsubT = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'todos'), (s) => {
        setTodos(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.userId === appUser?.id));
    }, (err) => console.error(err));

    const unsubSS = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'shared_schedules'), (s) => {
        const m = {}; s.docs.forEach(d => { m[d.data().date] = d.data().items || []; });
        setSharedSchedules(m);
    }, (err) => console.error(err));

    const peRef = doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main');
    const unsubPE = onSnapshot(peRef, async (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setPhoneEduData(d);
        setPhoneEduMemoEdit(d.memo || '');
      } else {
        const o = (v='',c='') => ({v,c});
        const initial = {
          columns: ['오사','한국','이레원','오공','사칠','사팔','키위','권실장','입금','전국','카카오','꽃뿐이','사구','문자'],
          rows: [
            {name:'문선욱',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'박소영',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'손수진',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'손미경',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('X'),전국:o('X'),카카오:o('X'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'주가영',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('X','yellow'),전국:o('X','yellow'),카카오:o('X','yellow'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'최승애',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('X','pink'),전국:o('X','pink'),카카오:o('X','pink'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'강은진',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('X'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'박재현',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('X'),권실장:o('X'),입금:o('X'),전국:o('X'),카카오:o('X'),꽃뿐이:o('X'),사구:o('X'),문자:o('O')}},
            {name:'명지수',cells:{오사:o(''),한국:o(''),이레원:o(''),오공:o(''),사칠:o('7.20~8.02'),사팔:o(''),키위:o('X'),권실장:o('X'),입금:o('X'),전국:o('X'),카카오:o('X'),꽃뿐이:o('X'),사구:o('X'),문자:o('7.6~7.19')}},
            {name:'손수령',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'윤라연',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'이미금',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'박수빈',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('X'),권실장:o('X'),입금:o('X'),전국:o('X'),카카오:o('X'),꽃뿐이:o('X'),사구:o('X'),문자:o('O')}},
            {name:'박해니',cells:{오사:o('6.16~6.30'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('X'),권실장:o('X'),입금:o('X'),전국:o('X'),카카오:o('X'),꽃뿐이:o('X'),사구:o('X'),문자:o('O')}},
            {name:'손주영',cells:{오사:o('7.13~7.27'),한국:o('6.26~7.10'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('X'),권실장:o('X'),입금:o('X'),전국:o('X'),카카오:o('X'),꽃뿐이:o('X'),사구:o('X'),문자:o('O')}},
            {name:'이재희',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'이경옥',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'김가은',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('O'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'김보혜',cells:{오사:o('O'),한국:o('O'),이레원:o('O'),오공:o('O'),사칠:o('O'),사팔:o('O'),키위:o('O'),권실장:o('O'),입금:o('X'),전국:o('O'),카카오:o('O'),꽃뿐이:o('O'),사구:o('O'),문자:o('O')}},
            {name:'박언정',cells:{오사:o(''),한국:o(''),이레원:o(''),오공:o('7.1~7.15'),사칠:o('6.15~6.30'),사팔:o('O'),키위:o(''),권실장:o(''),입금:o(''),전국:o(''),카카오:o(''),꽃뿐이:o(''),사구:o(''),문자:o('O')}},
            {name:'강아름',cells:{오사:o(''),한국:o(''),이레원:o(''),오공:o(''),사칠:o(''),사팔:o('7.20~8.02'),키위:o(''),권실장:o(''),입금:o(''),전국:o(''),카카오:o(''),꽃뿐이:o(''),사구:o(''),문자:o('7.6~7.19')}},
          ],
          memo: '사구폰 교육 순서 예정\n박주원 > 김가은 > 손수진 > 손미경 > 이미금 > 주가영 > 최승애 > 강은진 / 완료'
        };
        await setDoc(peRef, initial);
      }
    }, (err) => console.error(err));

    const seRef = doc(db, 'artifacts', appId, 'public', 'data', 'security_education', 'main');
    const unsubSE = onSnapshot(seRef, async (snap) => {
      if (snap.exists()) {
        setSecEduData(snap.data());
      } else {
        const initial = {
          rows: [
            {company:'플라워일번지',name:'손수진',dates:{}},
            {company:'더조은사람들',name:'손수령',dates:{}},
            {company:'더조은사람들',name:'문선욱',dates:{}},
            {company:'더조은사람들',name:'박소영',dates:{}},
            {company:'더조은사람들',name:'이경옥',dates:{}},
            {company:'더조은사람들',name:'이재희',dates:{}},
            {company:'더조은사람들',name:'김숙정',dates:{}},
            {company:'플라워일번지',name:'유혜미',dates:{}},
            {company:'더조은사람들',name:'손미경',dates:{}},
            {company:'더조은사람들',name:'김가은',dates:{}},
            {company:'더조은사람들',name:'윤라연',dates:{}},
            {company:'채움플라워',name:'이미금',dates:{}},
            {company:'더조은사람들',name:'주가영',dates:{}},
            {company:'더조은사람들',name:'최승애',dates:{}},
            {company:'더조은사람들',name:'강은진',dates:{}},
            {company:'채움플라워',name:'박수빈',dates:{}},
            {company:'더조은사람들',name:'김보혜',dates:{}},
            {company:'더조은사람들',name:'김경미',dates:{}},
            {company:'채움플라워',name:'박해니',dates:{}},
            {company:'채움플라워',name:'박재현',dates:{}},
            {company:'해솔플라워',name:'손주영',dates:{}},
            {company:'정플라워',name:'박언정',dates:{}},
            {company:'해솔플라워',name:'명지수',dates:{}},
            {company:'해솔플라워',name:'강아름',dates:{}},
          ]
        };
        await setDoc(seRef, initial);
      }
    }, (err) => console.error(err));

    const isAdmin = ['admin', 'user1', 'user2', ...AI_EXTRA_IDS].includes(appUser?.id) || ['대표', '사장', '이사'].includes(appUser?.role);
    let unsubAI = () => {}, unsubAICfg = () => {};
    if (isAdmin) {
      unsubAI = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'ai_summaries'), (s) => {
        setAiHistory(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')));
      }, (err) => console.error(err));
      unsubAICfg = onSnapshot(doc(db, 'artifacts', appId, 'public', 'data', 'ai_config', 'main'), (snap) => {
        setAiGuideline(snap.exists() ? (snap.data().guideline || '') : '');
      }, (err) => console.error(err));
    }

    return () => { unsubP(); unsubL(); unsubN(); unsubM(); unsubS(); unsubT(); unsubSS(); unsubPE(); unsubSE(); unsubAI(); unsubAICfg(); };
  }, [user, appUser, appId]);

  const peCell = (data) => typeof data === 'string' ? { v: data, c: '' } : (data || { v: '', c: '' });

  const handlePhoneEduCellSave = async (value, color) => {
    if (!phoneEduData || !selectedPhoneEduCell) return;
    const prev = peCell(phoneEduData.rows.find(r => r.name === selectedPhoneEduCell.rowName)?.cells?.[selectedPhoneEduCell.col]);
    const newCell = { v: value, c: color !== undefined ? color : prev.c };
    const updatedRows = phoneEduData.rows.map(row =>
      row.name === selectedPhoneEduCell.rowName
        ? { ...row, cells: { ...row.cells, [selectedPhoneEduCell.col]: newCell } }
        : row
    );
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), { ...phoneEduData, rows: updatedRows });
      setShowPhoneEduCellModal(false);
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handlePhoneEduCellColorOnly = async (color) => {
    if (!phoneEduData || !selectedPhoneEduCell) return;
    const prev = peCell(phoneEduData.rows.find(r => r.name === selectedPhoneEduCell.rowName)?.cells?.[selectedPhoneEduCell.col]);
    const updatedRows = phoneEduData.rows.map(row =>
      row.name === selectedPhoneEduCell.rowName
        ? { ...row, cells: { ...row.cells, [selectedPhoneEduCell.col]: { v: prev.v, c: color } } }
        : row
    );
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), { ...phoneEduData, rows: updatedRows });
      setShowPhoneEduCellModal(false);
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleAddPhoneEduCol = async () => {
    if (!newColName.trim() || !phoneEduData) return;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), {
        ...phoneEduData, columns: [...phoneEduData.columns, newColName.trim()]
      });
      setNewColName(''); setShowAddColModal(false); showMsg('열이 추가되었습니다.');
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleDeletePhoneEduCol = async (colName) => {
    if (!phoneEduData || !window.confirm(`'${colName}' 열을 삭제하시겠습니까?`)) return;
    const updatedCols = phoneEduData.columns.filter(c => c !== colName);
    const updatedRows = phoneEduData.rows.map(row => {
      const newCells = { ...row.cells }; delete newCells[colName]; return { ...row, cells: newCells };
    });
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), { ...phoneEduData, columns: updatedCols, rows: updatedRows });
      showMsg('열이 삭제되었습니다.');
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleAddPhoneEduEmployee = async () => {
    if (!newEmployeeName.trim() || !phoneEduData) return;
    if (phoneEduData.rows.find(r => r.name === newEmployeeName.trim())) {
      showMsg('이미 존재하는 사원명입니다.', 'error'); return;
    }
    const emptyCells = {};
    phoneEduData.columns.forEach(col => { emptyCells[col] = { v: '', c: '' }; });
    const updatedRows = [...phoneEduData.rows, { name: newEmployeeName.trim(), cells: emptyCells }];
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), { ...phoneEduData, rows: updatedRows });
      setNewEmployeeName(''); setShowAddEmployeeModal(false); showMsg('사원이 추가되었습니다.');
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleDeletePhoneEduEmployee = async (name) => {
    if (!phoneEduData || !window.confirm(`'${name}' 사원을 삭제하시겠습니까?`)) return;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), {
        ...phoneEduData, rows: phoneEduData.rows.filter(r => r.name !== name)
      });
      showMsg('사원이 삭제되었습니다.');
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const getSecEduMonths = (year) => {
    return Array.from({length:12},(_,i)=>`${year}-${String(i+1).padStart(2,'0')}`);
  };

  const handleSecEduCellSave = async (dateVal) => {
    if (!secEduData || !selectedSecEduCell) return;
    const updatedRows = secEduData.rows.map(row =>
      (row.name === selectedSecEduCell.name && row.company === selectedSecEduCell.company)
        ? { ...row, dates: { ...(row.dates||{}), [selectedSecEduCell.monthKey]: dateVal } }
        : row
    );
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'security_education', 'main'), { ...secEduData, rows: updatedRows });
      setShowSecEduCellModal(false);
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleAddSecEduEmployee = async () => {
    const company = newSecEduEmployee.company === '직접입력' ? (newSecEduEmployee.companyCustom || '').trim() : newSecEduEmployee.company.trim();
    if (!newSecEduEmployee.name.trim() || !company || !secEduData) return;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'security_education', 'main'), {
        ...secEduData,
        rows: [...secEduData.rows, { company, name: newSecEduEmployee.name.trim(), dates: {} }]
      });
      setNewSecEduEmployee({ name: '', company: '', companyCustom: '' });
      setShowAddSecEduEmployeeModal(false);
      showMsg('사원이 추가되었습니다.');
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleDeleteSecEduEmployee = async (name, company) => {
    if (!secEduData || !window.confirm(`'${name}' 사원을 삭제하시겠습니까?`)) return;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'security_education', 'main'), {
        ...secEduData, rows: secEduData.rows.filter(r => !(r.name === name && r.company === company))
      });
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleMoveSecEduEmployee = async (index, dir) => {
    if (!secEduData) return;
    const rows = [...secEduData.rows];
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    [rows[index], rows[target]] = [rows[target], rows[index]];
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'security_education', 'main'), { ...secEduData, rows });
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const printLandscape = () => {
    document.body.classList.add('print-landscape');
    const style = document.createElement('style');
    style.id = 'landscape-page-style';
    style.textContent = '@media print { @page { size: A4 landscape !important; margin: 8mm !important; } }';
    document.head.appendChild(style);
    window.print();
    setTimeout(() => {
      document.body.classList.remove('print-landscape');
      const el = document.getElementById('landscape-page-style');
      if (el) el.remove();
    }, 1000);
  };

  const handleMovePhoneEduEmployee = async (index, dir) => {
    if (!phoneEduData) return;
    const rows = [...phoneEduData.rows];
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    [rows[index], rows[target]] = [rows[target], rows[index]];
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), { ...phoneEduData, rows });
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleSetColColor = async (col, color) => {
    if (!phoneEduData) return;
    const updatedColColors = { ...(phoneEduData.columnColors || {}), [col]: color };
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), { ...phoneEduData, columnColors: updatedColColors });
      setShowColColorPicker(null);
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  const handleSavePhoneEduMemo = async () => {
    if (!phoneEduData) return;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'phone_education', 'main'), { ...phoneEduData, memo: phoneEduMemoEdit });
      showMsg('메모가 저장되었습니다.');
    } catch (err) { showMsg('저장 실패', 'error'); }
  };

  useEffect(() => {
    if (workStatusSubTab === 'security-edu' && secEduData && secEduTableRef.current) {
      const today = new Date();
      const mk = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}`;
      const el = document.getElementById(`sec-month-${mk}`);
      if (el && secEduTableRef.current) {
        secEduTableRef.current.scrollLeft = el.offsetLeft - 170;
      }
    }
  }, [workStatusSubTab, secEduData]);

  useEffect(() => { logsRef.current = logs; }, [logs]);

  useEffect(() => {
    if (activeTab === 'write' && appUser) {
      const existing = logsRef.current.find(l => l.userId === appUser.id && l.date === logDate);
      setContent(existing ? existing.content : '✔️ ');
    }
  }, [logDate, activeTab, appUser]);

  const displayLogs = useMemo(() => {
    if (!appUser) return [];
    // 조회에는 제출된 일지만 노출 (임시저장은 작성 탭 본인 칸에서만 보임)
    let filtered = logs.filter(l => l.submitted && canSeeDataOf(l.userId, 'submitted'));
    if (filterMember !== 'all') filtered = filtered.filter(l => l.userId === filterMember);
    let result = filtered.filter(l => l.date >= appliedStartDate && l.date <= appliedEndDate);
    
    return result.sort((a, b) => {
      const pA = RANK_PRIORITY[profiles[a.userId]?.role] ?? 99;
      const pB = RANK_PRIORITY[profiles[b.userId]?.role] ?? 99;
      if (pA !== pB) return pA - pB;
      return (a.userName || "").localeCompare(b.userName || "", 'ko') || a.date.localeCompare(b.date);
    });
  }, [logs, appliedStartDate, appliedEndDate, filterMember, canSeeDataOf, profiles, RANK_PRIORITY, appUser]);

  const filteredMeetings = useMemo(() => {
    if (!appUser) return [];
    return meetings.filter(m => {
      if (!canSeeDataOf(m.userId, m.status)) return false;
      if (meetingFilterMember !== 'all' && m.userId !== meetingFilterMember) return false;
      const inRange = m.date >= appliedMeetingRange.start && m.date <= appliedMeetingRange.end;
      const matchSearch = meetingSearch === '' || (m.title && m.title.includes(meetingSearch)) || (m.content && m.content.includes(meetingSearch));
      return inRange && matchSearch;
    }).sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  }, [meetings, appliedMeetingRange, canSeeDataOf, meetingFilterMember, meetingSearch, appUser]);

  if (isAuthenticating) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <Flower className="text-pink-600 w-12 h-12 animate-bounce mb-4" />
        <p className="font-black text-slate-400 text-sm">연결 중입니다...</p>
      </div>
    );
  }

  if (!appUser) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-[28px] shadow-xl p-9 text-center border border-slate-100">
          <div className="w-14 h-14 rounded-full bg-pink-500 flex items-center justify-center mx-auto mb-4"><Flower className="text-white w-7 h-7" /></div>
          <h1 className="text-2xl font-black tracking-tight font-sans">플라워오피스</h1>
          <p className="text-xs font-bold text-slate-400 mb-8 mt-1.5">사내 업무 관리</p>
          <form onSubmit={handleLogin} className="space-y-3 text-left font-sans">
            <input name="loginId" placeholder="아이디" required className="w-full px-5 py-4 bg-slate-50 rounded-2xl outline-none border border-slate-100 focus:border-pink-300 transition-all font-bold text-sm" />
            <input name="loginPw" type="password" placeholder="비밀번호" required className="w-full px-5 py-4 bg-slate-50 rounded-2xl outline-none border border-slate-100 focus:border-pink-300 transition-all font-bold text-sm" />
            {loginError && <div className="text-red-500 text-xs font-bold px-4 py-3 bg-red-50 rounded-2xl flex flex-col gap-1 border border-red-100 animate-pulse">
                <div className="flex items-center gap-2 font-black"><AlertTriangle size={14}/> 오류 발생</div>
                <div className="font-normal opacity-80">{loginError}</div>
            </div>}
            <button type="submit" className="w-full bg-pink-500 hover:bg-pink-600 text-white font-black py-4 rounded-2xl shadow-lg shadow-pink-100 active:scale-95 transition-all text-sm">로그인</button>
          </form>
          <p className="mt-6 text-[10px] text-slate-300">© 플라워오피스. All rights reserved.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 md:pb-0 font-sans tracking-tight font-bold overflow-x-hidden">
      {message.text && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[1000] px-6 py-3.5 rounded-2xl shadow-2xl font-black bg-slate-900 text-white animate-in slide-in-from-top-4 text-xs whitespace-nowrap">
          {message.text}
        </div>
      )}

      {/* 상단 네비게이션 */}
      <nav className="bg-white border-b border-slate-100 h-16 md:h-[72px] flex items-center justify-between px-4 md:px-8 sticky top-0 z-[100] print:hidden">
        <div className="flex items-center gap-3 md:gap-8 font-sans truncate">
          <div className="flex-shrink-0 flex items-center gap-2.5">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-pink-500 flex items-center justify-center flex-shrink-0"><Flower className="text-white w-4 h-4 md:w-5 md:h-5"/></div>
            <div className="flex flex-col leading-none">
              <span className="font-black text-[15px] md:text-[17px] text-slate-900 tracking-tight">플라워오피스</span>
              <span className="hidden md:block text-[10px] font-bold text-slate-400 mt-1">사내 업무 관리</span>
            </div>
          </div>
          <div className="hidden md:flex gap-0.5 bg-slate-50 p-1 rounded-full border border-slate-100">
            {navItems.map(item => {
              const Icon = navIconFor(item.id);
              return (
                <button key={item.id} onClick={() => setActiveTab(item.id)} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold transition-all ${activeTab === item.id ? 'bg-white shadow-sm text-pink-500' : 'text-slate-500 hover:text-slate-900'}`}>
                  <Icon size={15} strokeWidth={2.4} /> {item.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-1 md:gap-3 font-bold flex-shrink-0">
          <span className="hidden lg:block text-[12px] font-bold text-slate-400 mr-1">{koreanToday}</span>
          <button onClick={() => setShowMyProfileModal(true)} className="flex items-center gap-2 pl-2 pr-1 py-1 hover:bg-slate-50 rounded-full transition-all">
             <span className="hidden sm:inline font-bold text-[12px] text-slate-600">{appUser?.name} {appUser?.role}</span>
             <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-black text-slate-500">{appUser?.name?.charAt(0)}</div>
          </button>
          {isSystemAdmin && (
            <button onClick={enableNotifications} title="알림 켜기" className="p-2 text-slate-300 hover:text-pink-500 transition-colors"><Bell size={22}/></button>
          )}
          {isSystemAdmin && (
            <button onClick={() => setShowUserManageModal(true)} className="p-2 text-slate-300 hover:text-pink-500 transition-colors"><UserCog size={22}/></button>
          )}
          <button onClick={() => { setAppUser(null); localStorage.removeItem('flower_log_user_official'); }} className="p-2 text-slate-300 hover:text-red-500 transition-colors"><LogOut size={22}/></button>
        </div>
      </nav>

      {/* 모바일 하단 탭 바 */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t flex items-center justify-around h-16 pb-2 px-2 z-[1000] shadow-[0_-4px_16px_rgba(0,0,0,0.06)] print:hidden">
        {navItems.map(item => {
          const Icon = navIconFor(item.id);
          const active = activeTab === item.id;
          return (
            <button key={item.id} onClick={() => setActiveTab(item.id)} className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-all active:scale-95 ${active ? 'text-pink-500' : 'text-slate-400'}`}>
              <Icon size={21} strokeWidth={active ? 2.6 : 2} />
              <span className={`text-[10px] ${active ? 'font-black' : 'font-bold'}`}>{item.label}</span>
            </button>
          )
        })}
      </div>

      {/* 메인 레이아웃 */}
      <main className={`mx-auto p-2 md:p-10 font-bold transition-all ${activeTab === 'schedule' || activeTab === 'shared-schedule' || activeTab === 'work-status' || activeTab === 'meeting' ? 'max-w-7xl' : 'max-w-4xl'}`}>
        {activeTab === 'notice' && (
          <div className="space-y-6 animate-in fade-in max-w-2xl mx-auto pb-10 px-2">
            <h2 className="text-xl md:text-2xl font-black flex items-center gap-3"><Bell className="text-pink-600" size={26}/> 공지사항</h2>
            {notices.map(n => (
              <div key={n.id} className="bg-white p-5 md:p-10 rounded-[32px] border shadow-sm relative group font-sans hover:shadow-xl transition-all border-slate-50">
                <h3 className="text-xl md:text-2xl font-black mb-5 leading-snug tracking-tight">{n.title}</h3>
                <div className="text-slate-700 whitespace-pre-wrap leading-relaxed font-bold text-[15px] md:text-base mb-8">{n.content}</div>
                <div className="text-[10px] md:text-[11px] text-slate-300 pt-5 border-t uppercase tracking-widest font-black flex justify-between">
                  <span>작성: {n.authorName}</span>
                  <span>{n.createdAt ? new Date(n.createdAt.seconds * 1000).toLocaleDateString() : '날짜정보없음'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'shared-schedule' && (
          <div className="bg-white p-2 md:p-10 rounded-[32px] md:rounded-[40px] border shadow-xl overflow-x-auto font-bold font-sans animate-in fade-in duration-300 no-scrollbar">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 font-black gap-5 px-2 pt-2">
              <div>
                <h2 className="text-xl md:text-2xl font-black flex items-center gap-3 uppercase tracking-tight"><CalendarIcon className="text-pink-600" size={28} /> 공유 일정표</h2>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black bg-pink-50 border border-pink-100 text-pink-700"><span className="w-2 h-2 rounded-full bg-pink-500 flex-shrink-0"></span>신입/교육</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black bg-blue-50 border border-blue-100 text-blue-700"><span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>주요일정</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black bg-amber-50 border border-amber-100 text-amber-700"><span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>매월 정기</span>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-slate-50 p-3 rounded-2xl border shadow-inner self-center md:self-auto">
                <button onClick={() => setSharedCurrentDate(new Date(sharedCurrentDate.getFullYear(), sharedCurrentDate.getMonth() - 1))} className="p-2.5 hover:bg-white hover:shadow rounded-xl transition-all active:scale-90"><ChevronLeft size={24}/></button>
                <span className="tracking-tighter font-black text-xl md:text-2xl min-w-[140px] text-center">{sharedCurrentDate.getFullYear()}년 {sharedCurrentDate.getMonth() + 1}월</span>
                <button onClick={() => setSharedCurrentDate(new Date(sharedCurrentDate.getFullYear(), sharedCurrentDate.getMonth() + 1))} className="p-2.5 hover:bg-white hover:shadow rounded-xl transition-all active:scale-90"><ChevronRight size={24}/></button>
              </div>
            </div>

            <div className="grid grid-cols-7 border-t border-l rounded-2xl overflow-hidden min-w-[700px]">
              {['일', '월', '화', '수', '목', '금', '토'].map(d => (
                <div key={d} className={`p-4 text-center border-r border-b font-black text-xs uppercase tracking-widest ${d === '일' ? 'text-red-500' : d === '토' ? 'text-blue-500' : 'text-slate-400'}`}>{d}</div>
              ))}
              {Array.from({ length: getFirstDayOfMonth(sharedCurrentDate.getFullYear(), sharedCurrentDate.getMonth()) }).map((_, i) => (
                <div key={`empty-${i}`} className="p-4 border-r border-b bg-slate-50/30 h-24 md:h-32"></div>
              ))}
              {Array.from({ length: getDaysInMonth(sharedCurrentDate.getFullYear(), sharedCurrentDate.getMonth()) }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${sharedCurrentDate.getFullYear()}-${String(sharedCurrentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isToday = dateStr === getToday();
                const holiday = KR_HOLIDAYS[dateStr];
                const dayItems = sharedSchedules[dateStr] || [];
                return (
                  <div key={day} onClick={() => { setSelectedSharedDate(dateStr); setTempSharedItems(dayItems.map(it => ({...it}))); setRemovedSharedRanges([]); setShowSharedScheduleModal(true); }} className={`p-2 border-r border-b h-24 md:h-32 cursor-pointer hover:bg-slate-50 transition-all ${isToday ? 'bg-pink-50/30' : 'bg-white'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-xs font-black ${isToday ? 'w-6 h-6 bg-pink-600 text-white rounded-full flex items-center justify-center' : holiday ? 'text-red-500' : 'text-slate-900'}`}>{day}</span>
                      {holiday && <span className="text-[9px] font-black text-red-500 truncate max-w-[40px]">{holiday}</span>}
                    </div>
                    <div className="space-y-1 overflow-y-auto max-h-[80%] no-scrollbar">
                      {dayItems.map((item, idx) => {
                        const color = SCHEDULE_COLORS.find(c => c.id === item.color) || SCHEDULE_COLORS[0];
                        return (
                          <div key={idx} className={`px-2 py-0.5 rounded text-[10px] font-black border-l-2 truncate ${color.bg} ${color.border} text-slate-700`} title={item.authorName ? `${item.authorName} 등록` : ''}>
                            {item.text}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="bg-white p-2 md:p-10 rounded-[32px] md:rounded-[40px] border shadow-xl overflow-x-auto font-bold font-sans animate-in fade-in duration-300 no-scrollbar">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 font-black gap-5 px-2 pt-2">
                <h2 className="text-xl md:text-2xl font-black flex items-center gap-3 uppercase tracking-tight"><CalendarDays className="text-pink-600" size={28} /> 일정표</h2>
                <div className="flex items-center gap-4 bg-slate-50 p-3 rounded-2xl border shadow-inner self-center md:self-auto">
                  <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))} className="p-2.5 hover:bg-white hover:shadow rounded-xl transition-all active:scale-90"><ChevronLeft size={24}/></button>
                  <span className="tracking-tighter font-black text-xl md:text-2xl min-w-[140px] text-center">{currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월</span>
                  <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))} className="p-2.5 hover:bg-white hover:shadow rounded-xl transition-all active:scale-90"><ChevronRight size={24}/></button>
                </div>
            </div>
            
            <div className="grid grid-cols-7 border-t border-l rounded-2xl overflow-hidden min-w-[700px]">
              {['일', '월', '화', '수', '목', '금', '토'].map(d => (
                <div key={d} className={`p-4 text-center border-r border-b font-black text-xs uppercase tracking-widest ${d === '일' ? 'text-red-500' : d === '토' ? 'text-blue-500' : 'text-slate-400'}`}>{d}</div>
              ))}
              {Array.from({ length: getFirstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth()) }).map((_, i) => (
                <div key={`empty-${i}`} className="p-4 border-r border-b bg-slate-50/30 h-24 md:h-32"></div>
              ))}
              {Array.from({ length: getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth()) }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isToday = dateStr === getToday();
                const holiday = KR_HOLIDAYS[dateStr];
                const dayItems = schedules[dateStr] || [];
                return (
                  <div key={day} onClick={() => { setSelectedDate(dateStr); setTempScheduleItems(dayItems.map(it => ({...it}))); setRemovedRanges([]); setShowScheduleModal(true); }} className={`p-2 border-r border-b h-24 md:h-32 cursor-pointer hover:bg-slate-50 transition-all ${isToday ? 'bg-pink-50/30' : 'bg-white'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-xs font-black ${isToday ? 'w-6 h-6 bg-pink-600 text-white rounded-full flex items-center justify-center' : holiday ? 'text-red-500' : 'text-slate-900'}`}>{day}</span>
                      {holiday && <span className="text-[9px] font-black text-red-500 truncate max-w-[40px]">{holiday}</span>}
                    </div>
                    <div className="space-y-1 overflow-y-auto max-h-[80%] no-scrollbar">
                      {dayItems.map((item, idx) => {
                        const color = SCHEDULE_COLORS.find(c => c.id === item.color) || SCHEDULE_COLORS[0];
                        return (
                          <div key={idx} className={`px-2 py-0.5 rounded text-[10px] font-black border-l-2 truncate flex items-center gap-1 ${color.bg} ${color.border} ${item.done ? 'opacity-50 line-through decoration-slate-400 text-slate-400' : 'text-slate-700'}`}>
                            {item.done && <CheckCircle2 size={9} className="flex-shrink-0" />}{item.text}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'todo' && (
          <div className="space-y-6 animate-in fade-in max-w-2xl mx-auto pb-10 px-2 font-sans">
            <h2 className="text-xl md:text-2xl font-black flex items-center gap-3"><ListTodo className="text-pink-600" size={26}/> 할일 목록</h2>
            <form onSubmit={handleAddTodo} className="bg-white p-6 rounded-[28px] border shadow-sm flex flex-col sm:flex-row gap-4 items-center">
              <input type="date" value={todoDate} onChange={e => setTodoDate(e.target.value)} className="w-full sm:w-auto bg-slate-50 p-4 rounded-xl font-bold outline-none border shadow-inner text-sm" />
              <input value={todoInput} onChange={e => setTodoInput(e.target.value)} placeholder="오늘의 할 일을 입력하세요..." className="flex-1 bg-slate-50 p-4 rounded-xl font-bold outline-none border shadow-inner text-sm" />
              <button type="submit" className="w-full sm:w-auto bg-slate-900 text-white px-8 py-4 rounded-xl font-black shadow-lg active:scale-95 transition-all text-sm uppercase">Add</button>
            </form>
            
            <div className="space-y-3">
              {todos.sort((a,b) => (a.order ?? 0) - (b.order ?? 0)).map((todo, index) => (
                <div key={todo.id} className={`bg-white p-5 rounded-[24px] border shadow-sm flex items-center justify-between transition-all group ${todo.completed ? 'opacity-60 bg-slate-50/50' : 'hover:shadow-md'}`}>
                   <div className="flex items-center gap-4 flex-1">
                      <button onClick={() => handleToggleTodo(todo)} className={`p-1 rounded-full transition-all ${todo.completed ? 'text-pink-600' : 'text-slate-300 hover:text-slate-500'}`}>
                        {todo.completed ? <CheckCircle2 size={24} /> : <Circle size={24} />}
                      </button>
                      <div className="flex flex-col">
                        <span className={`text-[11px] font-black uppercase tracking-wider mb-0.5 ${todo.completed ? 'text-slate-400' : 'text-pink-500'}`}>{todo.date}</span>
                        {editingTodoId === todo.id ? (
                           <div className="flex gap-2">
                             <input value={editingTodoText} onChange={e => setEditingTodoText(e.target.value)} className="bg-slate-50 border-none outline-none font-bold text-sm p-1 rounded" />
                             <button onClick={async () => { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'todos', todo.id), { text: editingTodoText }); setEditingTodoId(null); }} className="text-blue-500"><Save size={16}/></button>
                           </div>
                        ) : (
                          <span className={`text-sm md:text-base font-bold text-slate-700 ${todo.completed ? 'line-through decoration-pink-300' : ''}`}>{todo.text}</span>
                        )}
                      </div>
                   </div>
                   <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button onClick={() => moveTodo(index, -1)} disabled={index === 0} className="p-2 text-slate-300 hover:text-slate-900 disabled:opacity-20"><ChevronUp size={20}/></button>
                      <button onClick={() => moveTodo(index, 1)} disabled={index === todos.length - 1} className="p-2 text-slate-300 hover:text-slate-900 disabled:opacity-20"><ChevronDown size={20}/></button>
                      <button onClick={() => { setEditingTodoId(todo.id); setEditingTodoText(todo.text); }} className="p-2 text-slate-300 hover:text-slate-900 ml-2"><Pencil size={18}/></button>
                      <button onClick={async () => { if(window.confirm('삭제하시겠습니까?')) await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'todos', todo.id)); }} className="p-2 text-slate-300 hover:text-red-500"><Trash2 size={18}/></button>
                   </div>
                </div>
              ))}
              {todos.length === 0 && <div className="text-center py-20 text-slate-300 font-black">기록된 할 일이 없습니다.</div>}
            </div>
          </div>
        )}

        {activeTab === 'write' && (
          <div className="bg-white p-4 md:p-14 rounded-[32px] md:rounded-[48px] border shadow-2xl animate-in fade-in max-w-4xl mx-auto border-slate-50 px-3">
            <div className="flex justify-between items-center mb-10 font-black flex-col sm:flex-row gap-5">
              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight flex items-center gap-4 self-start"><ClipboardList className="text-pink-600" size={30} /> 일지 작성</h2>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button onClick={() => setLogDate(getYesterday())} className={`px-4 py-3.5 rounded-2xl text-xs font-black transition-all active:scale-95 ${logDate === getYesterday() ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>어제</button>
                <button onClick={() => setLogDate(getToday())} className={`px-4 py-3.5 rounded-2xl text-xs font-black transition-all active:scale-95 ${logDate === getToday() ? 'bg-pink-600 text-white shadow-lg shadow-pink-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>오늘</button>
                <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)} className="flex-1 sm:w-auto bg-slate-50 p-3.5 px-5 rounded-2xl font-black outline-none shadow-inner text-sm border border-slate-50" />
              </div>
            </div>
            <textarea 
              ref={textareaRef}
              value={content} 
              onChange={e => setContent(e.target.value)} 
              onKeyDown={handleWorkLogKeyDown} 
              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              className="w-full overflow-y-auto bg-slate-50/50 rounded-[32px] md:rounded-[40px] p-6 md:p-12 text-[15px] md:text-lg outline-none leading-relaxed resize-none shadow-inner font-bold font-sans min-h-[450px] border border-slate-50 focus:bg-white transition-all focus:shadow-xl"
              placeholder="오늘의 업무 기록을 시작하세요..." 
            />
            <div className="mt-5">
              <button onClick={submitDayOff} className="px-6 py-3 bg-slate-100 text-slate-500 rounded-2xl font-black text-xs hover:bg-slate-200 active:scale-95 transition-all flex items-center gap-2"><CalendarIcon size={15}/> 휴무</button>
            </div>
            <div className="flex flex-col sm:flex-row gap-5 mt-3">
              <button onClick={() => saveWorkLog(content, false)} className="flex-1 bg-slate-100 py-5 rounded-[24px] text-slate-500 hover:bg-slate-200 transition-all font-black uppercase tracking-widest text-xs active:scale-95">임시저장</button>
              <button onClick={() => saveWorkLog(content, true)} className="flex-[2] bg-pink-600 text-white py-5 rounded-[24px] shadow-2xl shadow-pink-100 hover:bg-pink-700 active:scale-95 transition-all font-black uppercase tracking-widest text-xs">업무 완료 제출</button>
            </div>
          </div>
        )}

        {activeTab === 'view' && (
          <div className="bg-white p-3 md:p-10 rounded-[32px] md:rounded-[48px] border shadow-2xl animate-in fade-in font-bold font-sans max-w-full border-slate-50 print:p-0 print:border-none print:shadow-none">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-10 gap-8 font-black print:hidden px-2 pt-2">
              <h2 className="text-2xl md:text-3xl font-black flex items-center gap-5 font-sans uppercase tracking-tight"><Search className="text-pink-600" size={36}/> 업무 일지 조회</h2>
              <div className="flex flex-col w-full xl:w-auto gap-4">
                <div className="flex flex-wrap gap-2 justify-center sm:justify-end">
                  {['어제', '오늘', '이번주', '지난주'].map(m => (
                    <button key={m} onClick={() => setRangeMode(m)} className={`px-4 py-2.5 rounded-2xl text-[11px] font-black transition-all ${activeRangeMode === m ? 'bg-slate-900 text-white shadow-xl' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}>{m}</button>
                  ))}
                  {canUseAi && <button onClick={() => setShowAiConfirm(true)} className="flex items-center gap-1.5 bg-slate-900 text-white px-4 py-2.5 rounded-2xl shadow-lg active:scale-95 ml-2 text-[11px] font-black"><Flower size={14} className="text-pink-400"/> AI 요약</button>}
                  <button onClick={() => window.print()} className="bg-pink-600 text-white p-2.5 rounded-2xl shadow-lg active:scale-95"><Printer size={20}/></button>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-2xl shadow-inner border border-slate-100 w-full sm:w-auto justify-center">
                    <input type="date" value={appliedStartDate} onChange={e => setAppliedStartDate(e.target.value)} className="bg-transparent text-xs font-black outline-none w-[115px]" />
                    <span className="text-slate-300 text-xs">~</span>
                    <input type="date" value={appliedEndDate} onChange={e => setAppliedEndDate(e.target.value)} className="bg-transparent text-xs font-black outline-none w-[115px]" />
                  </div>
                  <select value={filterMember} onChange={e => setFilterMember(e.target.value)} className="w-full sm:w-auto bg-white px-4 py-3.5 rounded-2xl text-xs font-black shadow-sm outline-none border border-slate-200">
                    <option value="all">전 직원</option>
                    {activeUserList.filter(u => canSeeDataOf(u.id)).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="space-y-10 max-w-4xl mx-auto print:space-y-6">
              {(() => {
                const groups = [];
                const idx = {};
                displayLogs.forEach(log => {
                  if (idx[log.userId] === undefined) { idx[log.userId] = groups.length; groups.push({ userId: log.userId, userName: log.userName, logs: [] }); }
                  groups[idx[log.userId]].logs.push(log);
                });
                groups.forEach(g => g.logs.sort((a,b) => a.date.localeCompare(b.date)));
                return groups.map(group => (
                  <div key={group.userId} className="log-card bg-white p-6 md:p-12 rounded-[24px] md:rounded-[48px] border border-slate-100 shadow-md hover:shadow-2xl transition-all print:break-inside-avoid">
                    <div className="flex items-center gap-4 md:gap-6 mb-8 font-black print:mb-4">
                      <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-300 font-black text-xl md:text-3xl uppercase print:w-10 print:h-10 print:text-lg">{group.userName?.charAt(0)}</div>
                      <div className="flex items-center gap-4">
                        <span className="font-black text-slate-900 text-lg md:text-2xl tracking-tight print:text-base">{group.userName}</span>
                        <span className="text-[9px] md:text-[11px] bg-pink-50 text-pink-600 px-3 py-1 rounded-full font-black print:text-[8px]">{profiles[group.userId]?.role || '사원'}</span>
                      </div>
                    </div>
                    <div className="space-y-6 print:space-y-3">
                      {group.logs.map(log => (
                        <div key={log.id}>
                          <div className="flex items-center gap-2 mb-2.5 print:mb-1.5">
                            <span className="text-[11px] md:text-[13px] font-black text-pink-600 bg-pink-50 px-3 py-1 rounded-full flex items-center gap-1.5 print:text-[10px] print:bg-transparent print:px-0"><CalendarIcon size={13}/> {getFormattedDate(log.date)}</span>
                          </div>
                          <div className="bg-slate-50/50 p-5 md:p-8 rounded-[20px] md:rounded-[32px] border border-slate-50 shadow-inner font-sans tracking-tight print:bg-white print:p-3 print:border print:border-slate-100">
                            {log.content.split('\n').map((line, i) => (
                              <div key={i} className={`mb-2 flex items-start gap-2 ${line.includes('✔️') ? 'font-bold' : 'pl-7 text-slate-600 font-normal print:pl-5'}`}>
                                {line.includes('✔️') && <CheckCircle2 size={18} className="text-pink-500 mt-1 flex-shrink-0 print:w-3 print:h-3" />}
                                <span className="text-[15px] md:text-[14px] leading-relaxed print:text-[12px]">{line.replace('✔️', '').trim()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
              {displayLogs.length === 0 && <div className="text-center py-48 text-slate-200 font-black"><p className="text-xl">일지 기록이 없습니다.</p></div>}
            </div>
          </div>
        )}

        {activeTab === 'meeting' && (() => {
          const selectedMeeting = filteredMeetings.find(m => m.id === selectedMeetingId) || (!isWritingMeeting ? filteredMeetings[0] : null);
          return (
          <div className="animate-in fade-in font-sans pb-10 px-1">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 font-black mb-6 print:hidden">
               <h2 className="text-2xl md:text-3xl font-black flex items-center gap-4 uppercase tracking-tight"><Users2 className="text-pink-600" size={34}/> 회의록</h2>
               <div className="flex flex-col sm:flex-row flex-wrap w-full md:w-auto gap-3">
                 <div className="relative flex-1 sm:flex-none">
                    <input value={meetingSearchDraft} onChange={e => setMeetingSearchDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { setMeetingSearch(meetingSearchDraft.trim()); setAppliedMeetingRange(meetingRangeDraft); } }} placeholder="회의 검색..." className="w-full sm:w-56 pl-11 pr-4 py-3 bg-white border rounded-2xl text-sm font-bold shadow-sm outline-none focus:border-pink-300 transition-all" />
                    <Search size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                 </div>
                 <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-2xl px-3 py-1.5 shadow-sm">
                    <input type="date" value={meetingRangeDraft.start} max={meetingRangeDraft.end} onChange={e => setMeetingRangeDraft(r => ({...r, start: e.target.value}))} className="bg-transparent text-xs font-black text-slate-600 outline-none" />
                    <span className="text-slate-300 font-black text-xs">~</span>
                    <input type="date" value={meetingRangeDraft.end} min={meetingRangeDraft.start} onChange={e => setMeetingRangeDraft(r => ({...r, end: e.target.value}))} className="bg-transparent text-xs font-black text-slate-600 outline-none" />
                 </div>
                 <div className="flex gap-2">
                   <button onClick={() => { setMeetingSearch(meetingSearchDraft.trim()); setAppliedMeetingRange(meetingRangeDraft); }} className="bg-pink-100 text-pink-600 hover:bg-pink-200 flex items-center gap-1.5 px-4 py-3 rounded-2xl shadow-sm active:scale-95 font-black text-xs whitespace-nowrap transition-all"><Search size={16}/> 검색</button>
                   <select value={meetingFilterMember} onChange={e => setMeetingFilterMember(e.target.value)} className="flex-1 bg-white px-4 py-3 rounded-2xl text-xs font-black shadow-sm outline-none border border-slate-200">
                      <option value="all">전체 작성자</option>
                      {activeUserList.filter(u => canSeeDataOf(u.id)).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                   </select>
                   <button onClick={() => { setMeetingData({date: getToday(), title: '', participants: '', content: ''}); setIsWritingMeeting(true); setSelectedMeetingId(null); }} className="bg-pink-600 text-white flex items-center gap-1.5 px-4 py-3 rounded-2xl shadow-lg active:scale-95 font-black text-xs whitespace-nowrap"><Plus size={18}/> 새 회의록</button>
                 </div>
               </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
              {/* 좌측: 회의록 목록 (최근 10개) */}
              <div className="flex flex-col gap-3 print:hidden lg:max-h-[calc(100vh-180px)] lg:overflow-y-auto no-scrollbar pr-1">
                <span className="text-[11px] font-black text-slate-400 px-1 tracking-widest uppercase">회의록 {filteredMeetings.length}건</span>
                {filteredMeetings.length === 0 && <div className="text-center py-24 text-slate-200 font-black text-sm">해당 기간에 회의록이 없습니다.</div>}
                {filteredMeetings.map(m => {
                  const active = selectedMeeting && m.id === selectedMeeting.id && !isWritingMeeting;
                  return (
                    <button key={m.id} onClick={() => { setSelectedMeetingId(m.id); setIsWritingMeeting(false); }} className={`text-left bg-white p-4 rounded-2xl border transition-all active:scale-[0.98] ${active ? 'border-pink-300 shadow-md ring-1 ring-pink-200' : 'border-slate-100 shadow-sm hover:border-slate-200 hover:shadow'}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] font-black text-pink-500">{getFormattedDate(m.date)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-black ${m.status === 'submitted' ? 'bg-slate-900 text-white' : 'bg-amber-100 text-amber-600'}`}>{m.status === 'submitted' ? '제출됨' : '작성중'}</span>
                      </div>
                      <h4 className="text-sm font-black text-slate-900 leading-snug line-clamp-2 mb-1">{m.title || '(제목 없음)'}</h4>
                      <p className="text-[11px] text-slate-400 font-bold truncate">{m.participants || '참석자 미입력'}</p>
                    </button>
                  );
                })}
              </div>

              {/* 우측: 작성 폼 또는 선택된 회의록 상세 */}
              <div>
                {isWritingMeeting ? (
                  <div className="bg-white p-5 md:p-10 rounded-[28px] border shadow-xl space-y-6 animate-in fade-in font-bold font-sans print:hidden">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                       <div className="space-y-2">
                          <span className="text-[11px] text-slate-400 font-black px-2 tracking-widest uppercase">회의 날짜</span>
                          <input type="date" value={meetingData.date} onChange={e => setMeetingData({...meetingData, date: e.target.value})} className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold outline-none shadow-inner text-base" />
                       </div>
                       <div className="space-y-2">
                          <span className="text-[11px] text-slate-400 font-black px-2 tracking-widest uppercase">참석자</span>
                          <input placeholder="참석자 명단 입력" value={meetingData.participants} onChange={e => setMeetingData({...meetingData, participants: e.target.value})} className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold outline-none shadow-inner text-base" />
                       </div>
                    </div>
                    <div className="space-y-2">
                       <span className="text-[11px] text-pink-600 font-black px-2 tracking-widest uppercase">회의 주제 (안건)</span>
                       <input placeholder="회의 주제를 입력하세요" value={meetingData.title} onChange={e => setMeetingData({...meetingData, title: e.target.value})} className="w-full p-5 bg-slate-50 border-none rounded-2xl font-black text-lg outline-none shadow-inner focus:bg-white transition-all" />
                    </div>
                    <div className="space-y-2">
                       <span className="text-[11px] text-slate-400 font-black px-2 tracking-widest uppercase">회의 상세 기록</span>
                       <textarea placeholder="회의 내용을 기록하세요..." value={meetingData.content} onChange={e => setMeetingData({...meetingData, content: e.target.value})} className="w-full h-80 p-6 bg-slate-50 border-none rounded-[24px] font-bold outline-none resize-none shadow-inner text-base focus:bg-white transition-all" />
                    </div>
                    <div className="pt-1">
                       <button onClick={handleOrganizeMeeting} disabled={!meetingData.content || !meetingData.content.trim()} className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-4 rounded-2xl shadow-lg active:scale-95 transition-all font-black text-xs disabled:opacity-40 disabled:active:scale-100"><Flower size={16} className="text-pink-400"/> AI로 표준 양식 정리</button>
                       <p className="text-[10px] text-slate-400 font-bold text-center mt-2 px-2">자유롭게 적은 내용을 회사 표준 양식(개요·전달·건의·후속조치)으로 정리해줍니다. 원본은 작성자 본인만 볼 수 있게 저장됩니다.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 font-black pt-1">
                       <button onClick={() => setIsWritingMeeting(false)} className="flex-1 py-4 bg-slate-100 rounded-2xl font-black uppercase text-xs active:scale-95">작성 취소</button>
                       <button onClick={() => handleSaveMeeting('saved')} className="flex-1 py-4 bg-slate-200 text-slate-700 rounded-2xl font-black uppercase text-xs active:scale-95">임시 저장</button>
                       <button onClick={() => handleSaveMeeting('submitted')} className="flex-[2] bg-pink-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest shadow-xl text-xs active:scale-95">최종 제출</button>
                    </div>
                  </div>
                ) : selectedMeeting ? (
                  <div className="bg-white p-5 md:p-12 rounded-[28px] border shadow-md font-bold relative border-slate-100 print:border-none print:shadow-none print:p-0 print:rounded-none">
                    <div className="flex flex-col md:flex-row justify-between items-start mb-8 gap-5 font-black">
                      <div className="flex-1">
                         <div className="flex items-center gap-4 mb-4 flex-wrap print:mb-2">
                           <span className="text-[11px] font-black text-pink-500 uppercase tracking-widest print:text-[10px]">{getFormattedDate(selectedMeeting.date)}</span>
                           <span className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest print:hidden ${selectedMeeting.status === 'submitted' ? 'bg-slate-900 text-white shadow-sm' : 'bg-amber-100 text-amber-600'}`}>{selectedMeeting.status === 'submitted' ? '공식 제출됨' : '작성 중'}</span>
                         </div>
                         <h3 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight leading-snug mb-4 print:text-lg print:mb-2">{selectedMeeting.title}</h3>
                         <p className="text-[12px] md:text-[13px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-2.5 print:text-[10px]"><Users2 size={18} className="print:w-3 print:h-3"/> {selectedMeeting.participants}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 self-end md:self-start print:hidden">
                         <button onClick={() => handleMeetingWord(selectedMeeting)} className="flex items-center gap-1.5 px-3.5 py-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-2xl transition-all active:scale-95 font-black text-xs"><Save size={16}/> Word</button>
                         <button onClick={() => handleMeetingPdf(selectedMeeting)} className="flex items-center gap-1.5 px-3.5 py-2.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-2xl transition-all active:scale-95 font-black text-xs"><Save size={16}/> PDF</button>
                         <button onClick={() => handlePrintMeeting(selectedMeeting)} className="flex items-center gap-1.5 px-3.5 py-2.5 bg-pink-600 text-white hover:bg-pink-700 rounded-2xl transition-all active:scale-95 font-black text-xs shadow-sm"><Printer size={16}/> 출력</button>
                         <button onClick={() => { setMeetingData(selectedMeeting); setIsWritingMeeting(true); }} className="p-2.5 bg-slate-50 text-slate-300 hover:text-pink-600 rounded-2xl transition-all active:scale-90"><Pencil size={20}/></button>
                         {(isSystemAdmin || selectedMeeting.userId === appUser?.id) && <button onClick={async () => { if(window.confirm('삭제하시겠습니까?')) { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'meetings', selectedMeeting.id)); setSelectedMeetingId(null); } }} className="p-2.5 bg-slate-50 text-slate-100 hover:text-red-500 rounded-2xl transition-all active:scale-90"><Trash2 size={20}/></button>}
                      </div>
                    </div>
                    <div className="text-slate-700 bg-slate-50/50 p-6 md:p-10 rounded-[24px] font-bold border border-slate-50 shadow-inner text-[15px] md:text-base leading-relaxed tracking-tight font-sans print:bg-white print:p-4 print:text-[12px] print:border print:border-slate-100">
                      {renderMeetingBody(selectedMeeting.content)}
                    </div>
                    {selectedMeeting.rawContent && selectedMeeting.userId === appUser?.id && (
                      <div className="mt-4 print:hidden">
                        <button onClick={() => setShowMeetingRaw(v => !v)} className="text-[11px] font-black text-slate-400 hover:text-slate-600 flex items-center gap-1.5 px-2 py-1 active:scale-95 transition-all"><FileText size={14}/> {showMeetingRaw ? '원본 숨기기' : 'AI 정리 전 원본 보기 (나만 보임)'}</button>
                        {showMeetingRaw && (
                          <div className="mt-2 text-slate-500 whitespace-pre-wrap bg-amber-50/60 p-5 md:p-7 rounded-[20px] font-bold border border-amber-100 text-[13px] md:text-[14px] leading-relaxed tracking-tight">
                            {selectedMeeting.rawContent}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm text-center py-40 text-slate-200 font-black">
                    <Users2 size={48} className="mx-auto mb-4 opacity-40" />
                    <p className="text-lg">왼쪽에서 회의록을 선택하세요.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })()}
        {activeTab === 'work-status' && (
          <div className="space-y-4 animate-in fade-in font-bold font-sans">
            <div className="flex gap-2 bg-slate-100 p-1.5 rounded-full w-fit">
              {[{id:'phone-edu',label:'업무폰 교육'},{id:'security-edu',label:'보안교육'},{id:'log-status',label:'일지 제출현황'}].map(sub => (
                <button key={sub.id} onClick={() => setWorkStatusSubTab(sub.id)}
                  className={`px-5 py-2 rounded-full text-xs font-black transition-all ${workStatusSubTab === sub.id ? 'bg-white shadow text-pink-600 scale-105' : 'text-slate-500 hover:text-slate-800'}`}>
                  {sub.label}
                </button>
              ))}
            </div>

            {workStatusSubTab === 'security-edu' && (() => {
              const months = getSecEduMonths(secEduYear);
              const today = new Date();
              const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}`;
              const cy = today.getFullYear();
              const yearOptions = [cy, cy+1];
              const formatMonthLabel = (key) => {
                const [, m] = key.split('-');
                return `${parseInt(m)}월`;
              };
              return (
                <div className="bg-white p-4 md:p-8 rounded-[32px] border shadow-xl">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-4">
                    <div className="flex items-center gap-4 flex-wrap">
                      <h3 className="text-base font-black text-slate-800">보안교육현황</h3>
                      <div className="flex gap-1 bg-slate-100 p-1 rounded-full">
                        {yearOptions.map(y => (
                          <button key={y} onClick={() => setSecEduYear(y)}
                            className={`px-4 py-1.5 rounded-full text-xs font-black transition-all ${secEduYear === y ? 'bg-white shadow text-pink-600' : 'text-slate-500 hover:text-slate-800'}`}>
                            {y}년
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 print:hidden">
                      <button onClick={() => setShowAddSecEduEmployeeModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-2xl font-black text-xs active:scale-95 transition-all hover:bg-slate-200"><Plus size={14}/> 사원 추가</button>
                      <button onClick={printLandscape} className="flex items-center gap-2 px-4 py-2.5 bg-pink-600 text-white rounded-2xl font-black text-xs active:scale-95 transition-all shadow-lg"><Printer size={14}/> 출력</button>
                    </div>
                  </div>
                  {secEduData ? (
                    <div className="overflow-x-auto no-scrollbar" ref={secEduTableRef}>
                      <table className="sec-edu-table border-collapse text-xs font-bold" style={{minWidth: `${180 + months.length * 72}px`}}>
                        <thead>
                          <tr>
                            <th className="sticky left-0 z-20 bg-slate-50 border border-slate-100 px-4 py-3 text-center font-black text-slate-500 min-w-[90px]">소속회사</th>
                            <th className="sticky left-[90px] z-20 bg-slate-50 border border-slate-100 px-4 py-3 text-center font-black text-slate-500 min-w-[72px]">이름</th>
                            {months.map(mk => {
                              const { year, month } = formatMonthLabel(mk);
                              const isCurrent = mk === currentMonthKey;
                              return (
                                <th key={mk} id={`sec-month-${mk}`}
                                  className={`border border-slate-100 px-2 py-3 text-center font-black min-w-[72px] ${isCurrent ? 'bg-pink-50 text-pink-600' : 'bg-slate-50 text-slate-400'}`}>
                                  {formatMonthLabel(mk)}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {[...secEduData.rows].sort((a,b) => a.name.localeCompare(b.name,'ko')).map((row, ri) => (
                            <tr key={`${row.company}-${row.name}`} className="group/serow hover:bg-slate-50/50 transition-colors">
                              <td className="sticky left-0 z-10 bg-white border border-slate-100 px-3 py-2.5 text-center font-bold text-slate-700 text-[11px] min-w-[90px]">
                                <div className="flex items-center justify-center gap-1">
                                  <div className="flex flex-col opacity-0 group-hover/serow:opacity-100 transition-all flex-shrink-0">
                                    <button onClick={() => handleMoveSecEduEmployee(ri, -1)} className="text-slate-300 hover:text-slate-600 leading-none"><ChevronUp size={11}/></button>
                                    <button onClick={() => handleMoveSecEduEmployee(ri, 1)} className="text-slate-300 hover:text-slate-600 leading-none"><ChevronDown size={11}/></button>
                                  </div>
                                  <span>{row.company}</span>
                                </div>
                              </td>
                              <td className="sticky left-[90px] z-10 bg-white border border-slate-100 px-3 py-2.5 text-center font-black text-slate-800 min-w-[72px]">
                                <div className="flex items-center justify-center gap-1">
                                  <span>{row.name}</span>
                                  <button onClick={() => handleDeleteSecEduEmployee(row.name, row.company)} className="opacity-0 group-hover/serow:opacity-100 text-slate-200 hover:text-red-500 transition-all flex-shrink-0"><X size={11}/></button>
                                </div>
                              </td>
                              {months.map(mk => {
                                const dateVal = row.dates?.[mk] || '';
                                const isCurrent = mk === currentMonthKey;
                                const display = dateVal ? dateVal.slice(5).replace('-','/') : '';
                                return (
                                  <td key={mk}
                                    onClick={() => { setSelectedSecEduCell({ name: row.name, company: row.company, monthKey: mk }); setSecEduDateInput(dateVal); setShowSecEduCellModal(true); }}
                                    className={`border border-slate-100 px-2 py-2.5 text-center cursor-pointer transition-all hover:bg-slate-100 active:scale-95 select-none min-w-[72px] ${isCurrent ? 'bg-pink-50/30' : ''} ${dateVal ? 'text-slate-800 font-black' : 'text-slate-200'}`}>
                                    {display || '—'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-24 text-center text-slate-200 font-black">로딩 중...</div>
                  )}
                </div>
              );
            })()}

            {workStatusSubTab === 'log-status' && (() => {
              const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const now = new Date();
              const dow = now.getDay();
              const diffToMon = dow === 0 ? -6 : 1 - dow;
              let startStr, endStr;
              if (logStatusMode === 'week') {
                const mon = new Date(now); mon.setDate(now.getDate() + diffToMon);
                startStr = fmt(mon); endStr = getToday();
              } else if (logStatusMode === 'lastweek') {
                const mon = new Date(now); mon.setDate(now.getDate() + diffToMon - 7);
                const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
                startStr = fmt(mon); endStr = fmt(sun);
              } else {
                startStr = logStatusRange.start; endStr = logStatusRange.end;
              }
              const dates = [];
              const sd = new Date(startStr + 'T00:00:00'), ed = new Date(endStr + 'T00:00:00');
              for (let d = new Date(sd); d <= ed && dates.length < 62; d.setDate(d.getDate()+1)) dates.push(fmt(new Date(d)));
              const days = ['일','월','화','수','목','금','토'];
              const logMap = {};
              logs.forEach(l => { logMap[`${l.userId}_${l.date}`] = l; });
              const emps = activeUserList.filter(u => u.id !== 'admin' && u.name !== '최고 관리자');
              const statusOf = (uid, date) => { const l = logMap[`${uid}_${date}`]; if (!l) return 'X'; if (l.dayOff) return '휴무'; return l.submitted ? 'O' : 'X'; };
              return (
                <div className="bg-white p-4 md:p-8 rounded-[32px] border shadow-xl">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-4">
                    <div>
                      <h2 className="text-xl md:text-2xl font-black flex items-center gap-3 uppercase tracking-tight"><ClipboardList className="text-pink-600" size={28}/> 업무현황</h2>
                      <p className="text-xs text-slate-400 font-bold mt-1 ml-1">업무일지 제출현황</p>
                    </div>
                    <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-full">
                      {[{id:'week',label:'이번주'},{id:'lastweek',label:'지난주'},{id:'custom',label:'기간별'}].map(m => (
                        <button key={m.id} onClick={() => setLogStatusMode(m.id)} className={`px-4 py-2 rounded-full text-xs font-black transition-all ${logStatusMode === m.id ? 'bg-white shadow text-pink-600' : 'text-slate-500 hover:text-slate-800'}`}>{m.label}</button>
                      ))}
                    </div>
                  </div>

                  {logStatusMode === 'custom' && (
                    <div className="flex items-center gap-2 mb-4 bg-slate-50 p-2.5 rounded-2xl border border-slate-100 w-fit">
                      <input type="date" value={logStatusRange.start} onChange={e => setLogStatusRange(p => ({...p, start: e.target.value}))} className="bg-transparent text-xs font-black outline-none w-[120px]" />
                      <span className="text-slate-300 text-xs">~</span>
                      <input type="date" value={logStatusRange.end} onChange={e => setLogStatusRange(p => ({...p, end: e.target.value}))} className="bg-transparent text-xs font-black outline-none w-[120px]" />
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black bg-emerald-50 border border-emerald-100 text-emerald-700"><span className="font-black">O</span> 제출완료</span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black bg-red-50 border border-red-100 text-red-600"><span className="font-black">X</span> 미제출</span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black bg-slate-100 border border-slate-200 text-slate-500">휴무</span>
                  </div>

                  <div className="overflow-x-auto no-scrollbar">
                    <table className="border-collapse text-xs font-bold" style={{minWidth: `${90 + emps.length*54}px`}}>
                      <thead>
                        <tr>
                          <th className="sticky left-0 z-20 bg-slate-50 border border-slate-100 px-3 py-2.5 text-center font-black text-slate-500 min-w-[76px]">날짜</th>
                          {emps.map(e => (
                            <th key={e.id} className="border border-slate-100 px-1 py-2.5 text-center font-black text-slate-500 min-w-[54px]">
                              <div className="whitespace-nowrap text-[11px]">{e.name}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dates.map(date => {
                          const wd = new Date(date + 'T00:00:00').getDay();
                          const isToday = date === getToday();
                          return (
                            <tr key={date} className={isToday ? 'bg-pink-50/30' : ''}>
                              <td className={`sticky left-0 z-10 border border-slate-100 px-3 py-2 text-center font-black min-w-[76px] ${isToday ? 'bg-pink-50' : 'bg-white'} ${wd===0?'text-red-500':wd===6?'text-blue-500':'text-slate-700'}`}>
                                <span className="text-[11px]">{parseInt(date.slice(5,7))}/{parseInt(date.slice(8,10))} ({days[wd]})</span>
                              </td>
                              {emps.map(e => {
                                const s = statusOf(e.id, date);
                                const cls = s === 'O' ? 'text-emerald-600' : s === '휴무' ? 'text-slate-400' : 'text-red-400';
                                return <td key={e.id} className={`border border-slate-100 px-1 py-2 text-center font-black ${s === '휴무' ? 'text-[10px]' : 'text-sm'} ${cls}`}>{s}</td>;
                              })}
                            </tr>
                          );
                        })}
                        <tr className="bg-slate-50">
                          <td className="sticky left-0 z-10 bg-slate-50 border border-slate-100 px-3 py-2 text-center font-black text-slate-400 text-[10px]">제출 합계</td>
                          {emps.map(e => {
                            const cnt = dates.filter(d => statusOf(e.id, d) === 'O').length;
                            return <td key={e.id} className="border border-slate-100 px-1 py-2 text-center font-black text-slate-500 text-[11px]">{cnt}</td>;
                          })}
                        </tr>
                        {dates.length === 0 && <tr><td colSpan={emps.length+1} className="text-center py-16 text-slate-200 font-black">기간을 선택하세요.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {workStatusSubTab === 'phone-edu' && (
            <div className="bg-white p-4 md:p-8 rounded-[32px] border shadow-xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <div>
                  <h2 className="text-xl md:text-2xl font-black flex items-center gap-3 uppercase tracking-tight"><ClipboardList className="text-pink-600" size={28}/> 업무현황</h2>
                  <p className="text-xs text-slate-400 font-bold mt-1 ml-1">업무폰 교육현황</p>
                </div>
                <div className="flex gap-2 flex-wrap print:hidden">
                  <button onClick={() => setShowAddEmployeeModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-2xl font-black text-xs active:scale-95 transition-all hover:bg-slate-200"><Plus size={14}/> 사원 추가</button>
                  <button onClick={() => setShowAddColModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-2xl font-black text-xs active:scale-95 transition-all hover:bg-slate-200"><Plus size={14}/> 열 추가</button>
                  <button onClick={printLandscape} className="flex items-center gap-2 px-4 py-2.5 bg-pink-600 text-white rounded-2xl font-black text-xs active:scale-95 transition-all shadow-lg"><Printer size={14}/> 출력</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black bg-amber-50 border border-amber-100 text-amber-700"><span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>진행중</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black bg-pink-50 border border-pink-100 text-pink-700"><span className="w-2 h-2 rounded-full bg-pink-400 flex-shrink-0"></span>예정</span>
              </div>

              {phoneEduData ? (
                <div className="overflow-x-auto no-scrollbar">
                  <table className="w-full border-collapse text-xs font-bold min-w-[900px]">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="p-3 text-center font-black text-slate-500 border border-slate-100 sticky left-0 bg-slate-50 z-10 min-w-[80px]">사원명</th>
                        {phoneEduData.columns.map(col => {
                          const colColor = phoneEduData.columnColors?.[col] || '';
                          return (
                            <th key={col} className="p-0 font-black text-slate-500 border border-slate-100 text-center min-w-[70px] group relative">
                              <div className="px-3 pt-2.5 pb-1 flex flex-col items-center gap-1">
                                <div className="flex items-center gap-1 w-full justify-center">
                                  <span className="cursor-pointer hover:text-slate-800 transition-colors" onClick={() => setShowColColorPicker(showColColorPicker === col ? null : col)}>{col}</span>
                                  <button onClick={() => handleDeletePhoneEduCol(col)} className="opacity-0 group-hover:opacity-100 text-slate-200 hover:text-red-500 transition-all flex-shrink-0"><X size={10}/></button>
                                </div>
                                <div className="w-full h-1.5 rounded-full" style={{ background: colColor || 'transparent', border: colColor ? 'none' : '1px dashed #e2e8f0' }}></div>
                              </div>
                              {showColColorPicker === col && (
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-slate-200 rounded-2xl shadow-2xl p-3 z-50 flex gap-2 flex-wrap w-[140px]">
                                  {['','#fbbf24','#f472b6','#34d399','#60a5fa','#a78bfa','#fb923c','#94a3b8'].map(clr => (
                                    <button key={clr} onClick={() => handleSetColColor(col, clr)}
                                      className={`w-6 h-6 rounded-full border-2 transition-all active:scale-90 ${colColor === clr ? 'border-slate-700 scale-110' : 'border-transparent'}`}
                                      style={{ background: clr || '#f1f5f9' }}
                                      title={clr || '없음'}
                                    />
                                  ))}
                                </div>
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {phoneEduData.rows.map((row, ri) => (
                        <tr key={row.name} className={`group/row hover:bg-slate-50/50 transition-colors ${ri % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                          <td className="p-2 font-black text-slate-800 border border-slate-100 sticky left-0 bg-white z-10 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <div className="flex flex-col opacity-0 group-hover/row:opacity-100 transition-all flex-shrink-0">
                                <button onClick={() => handleMovePhoneEduEmployee(ri, -1)} className="text-slate-300 hover:text-slate-600 leading-none"><ChevronUp size={12}/></button>
                                <button onClick={() => handleMovePhoneEduEmployee(ri, 1)} className="text-slate-300 hover:text-slate-600 leading-none"><ChevronDown size={12}/></button>
                              </div>
                              <span>{row.name}</span>
                              <button onClick={() => handleDeletePhoneEduEmployee(row.name)} className="opacity-0 group-hover/row:opacity-100 text-slate-200 hover:text-red-500 transition-all flex-shrink-0"><X size={12}/></button>
                            </div>
                          </td>
                          {phoneEduData.columns.map(col => {
                            const cell = peCell(row.cells?.[col]);
                            const v = cell.v; const c = cell.c;
                            const isO = v === 'O';
                            const isX = v === 'X';
                            const isDate = v && v !== 'O' && v !== 'X';
                            const bgCls = c === 'yellow' ? 'bg-amber-100 text-amber-700' : c === 'pink' ? 'bg-pink-100 text-pink-700' : isX ? 'text-red-500' : v ? 'text-slate-700' : 'text-slate-300';
                            return (
                              <td key={col}
                                onClick={() => { setSelectedPhoneEduCell({ rowName: row.name, col, currentValue: v, currentColor: c }); setPhoneEduDateInput(isDate ? v : ''); setShowPhoneEduCellModal(true); }}
                                className={`p-2 border border-slate-100 text-center cursor-pointer transition-all hover:brightness-95 active:scale-95 select-none font-black text-xs ${bgCls}`}>
                                {v || '—'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-24 text-center text-slate-200 font-black">로딩 중...</div>
              )}

              {phoneEduData && (
                <div className="mt-6 p-5 bg-slate-50 rounded-[20px] border border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">메모</span>
                    <button onClick={handleSavePhoneEduMemo} className="px-4 py-1.5 bg-slate-900 text-white rounded-xl font-black text-[10px] uppercase active:scale-95">저장</button>
                  </div>
                  <textarea
                    value={phoneEduMemoEdit}
                    onChange={e => setPhoneEduMemoEdit(e.target.value)}
                    className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none resize-none leading-relaxed"
                    rows={3}
                  />
                </div>
              )}
            </div>
            )}
          </div>
        )}

        {activeTab === 'ai-history' && (
          <div className="space-y-5 animate-in fade-in max-w-3xl mx-auto pb-10 px-2 font-sans">
            <div>
              <h2 className="text-xl md:text-2xl font-black flex items-center gap-3"><Flower className="text-pink-500" size={26}/> AI 요약 기록</h2>
              <p className="text-xs text-slate-400 font-bold mt-1 ml-1">지금까지 생성한 AI 업무일지 요약을 다시 볼 수 있습니다 · 최고관리자 전용</p>
            </div>

            <div className="bg-white p-5 rounded-[24px] border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ClipboardList size={18} className="text-pink-500"/>
                  <span className="font-black text-slate-800 text-sm">AI 분석 기준 (회사 업무 용어·분류)</span>
                </div>
                <button onClick={() => { setGuidelineDraft(aiGuideline); setShowGuidelineModal(true); }} className="flex items-center gap-1 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl font-black text-[11px] active:scale-95 hover:bg-slate-200 transition-all"><Search size={13}/> 보기</button>
              </div>
              <p className="text-[11px] font-bold text-slate-400 mt-2 leading-relaxed">여기에 회사 고유 용어와 업무 분류 규칙을 적어두면, AI가 요약할 때 이 기준을 최우선으로 따릅니다. "보기"를 눌러 확인·수정하세요.</p>
            </div>
            {aiHistory.length > 0 && (() => {
              const opts = [];
              const seen = new Set();
              aiHistory.forEach(r => { if (!seen.has(r.userId)) { seen.add(r.userId); opts.push({ id: r.userId, name: r.userName }); } });
              opts.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'));
              return (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[12px] font-black text-slate-500">직원 검색</span>
                  <select value={aiHistoryFilter} onChange={e => setAiHistoryFilter(e.target.value)} className="bg-white px-4 py-2.5 rounded-2xl text-xs font-black shadow-sm outline-none border border-slate-200">
                    <option value="all">전체 ({aiHistory.length})</option>
                    {opts.map(o => <option key={o.id} value={o.id}>{o.name} ({aiHistory.filter(r => r.userId === o.id).length})</option>)}
                  </select>
                </div>
              );
            })()}
            <div className="space-y-3">
              {aiHistory.length === 0 && <div className="text-center py-24 text-slate-200 font-black">아직 생성된 AI 요약이 없습니다.</div>}
              {(() => {
                const filtered = aiHistory.filter(r => aiHistoryFilter === 'all' || r.userId === aiHistoryFilter);
                if (aiHistory.length > 0 && filtered.length === 0) return <div className="text-center py-16 text-slate-200 font-black text-sm">해당 직원의 요약 기록이 없습니다.</div>;
                return filtered.map(rec => (
                <div key={rec.id} onClick={() => openSavedSummary(rec)} className="group w-full text-left bg-white p-5 rounded-[24px] border border-slate-100 shadow-sm hover:shadow-md hover:border-pink-200 transition-all active:scale-[0.99] flex items-center gap-4 cursor-pointer">
                  <div className="w-11 h-11 rounded-full bg-pink-500 flex items-center justify-center text-white font-black flex-shrink-0">{rec.userName?.charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-black text-slate-800 text-[15px]">{rec.userName}</span>
                      <span className="text-[10px] font-black bg-pink-50 text-pink-600 px-2.5 py-0.5 rounded-full">일지 {rec.count}건</span>
                    </div>
                    <div className="text-[12px] font-bold text-slate-400 mt-0.5">{rec.startDate} ~ {rec.endDate}</div>
                    <div className="text-[11px] font-bold text-slate-300 mt-0.5">{rec.requesterName} · {rec.createdAt ? new Date(rec.createdAt).toLocaleString('ko-KR') : ''}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); deleteAiHistory(rec.id); }} className="p-2 text-slate-200 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all flex-shrink-0"><Trash2 size={18}/></button>
                </div>
                ));
              })()}
            </div>
          </div>
        )}
      </main>

      {/* 모달: 일정 상세 */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-lg rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95">
             <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black">{selectedDate} 일정 관리</h3>
                <button onClick={() => setShowScheduleModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={24}/></button>
             </div>
             <div className="space-y-4 mb-8">
                {tempScheduleItems.map((item, i) => (
                  <div key={i} className="bg-slate-50/70 rounded-2xl p-3 space-y-2 border border-slate-100">
                    <div className="flex gap-2 items-center">
                      <button onClick={() => {
                        const next = [...tempScheduleItems]; next[i] = {...next[i], done: !next[i].done}; setTempScheduleItems(next);
                      }} className={`p-1 rounded-full transition-all flex-shrink-0 ${item.done ? 'text-pink-600' : 'text-slate-300 hover:text-slate-500'}`}>
                        {item.done ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                      </button>
                      <select value={item.color} onChange={e => {
                        const next = [...tempScheduleItems]; next[i].color = e.target.value; setTempScheduleItems(next);
                      }} className="bg-white p-2 rounded-lg text-xs font-black border border-slate-100">
                        {SCHEDULE_COLORS.map(c => <option key={c.id} value={c.id}>{c.id}</option>)}
                      </select>
                      <input value={item.text} onChange={e => {
                        const next = [...tempScheduleItems]; next[i].text = e.target.value; setTempScheduleItems(next);
                      }} className={`flex-1 bg-white p-3 rounded-xl font-bold text-sm outline-none border border-slate-100 ${item.done ? 'line-through decoration-pink-300 text-slate-400' : ''}`} />
                      <button onClick={() => {
                        if (item.rangeId && item.rangeStart && item.rangeEnd) {
                          if (!window.confirm('기간 일정입니다. 기간 전체를 삭제할까요?')) return;
                          setRemovedRanges([...removedRanges, { id: item.rangeId, start: item.rangeStart, end: item.rangeEnd }]);
                        }
                        setTempScheduleItems(tempScheduleItems.filter((_, idx) => idx !== i));
                      }} className="text-red-500 p-2"><Trash2 size={20}/></button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap pl-1">
                      <span className="text-[11px] font-black text-slate-400">기간 종료일</span>
                      <input type="date" min={selectedDate} value={item.endDate || item.rangeEnd || ''} onChange={e => {
                        const next = [...tempScheduleItems]; next[i] = {...next[i], endDate: e.target.value}; setTempScheduleItems(next);
                      }} className="bg-white p-2 rounded-lg text-xs font-bold border border-slate-100 outline-none" />
                      {(item.endDate || item.rangeEnd) && (
                        <>
                          <span className="text-[11px] font-bold text-pink-600">{selectedDate} ~ {item.endDate || item.rangeEnd}</span>
                          <button onClick={() => {
                            const next = [...tempScheduleItems]; next[i] = {...next[i], endDate: ''}; setTempScheduleItems(next);
                          }} className="text-[11px] font-bold text-slate-400 underline">지우기</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                <button onClick={() => setTempScheduleItems([...tempScheduleItems, { text: '', color: 'pink', done: false }])} className="w-full py-3 border-2 border-dashed rounded-xl text-slate-400 hover:text-pink-600 hover:border-pink-200 transition-all flex items-center justify-center gap-2 font-black text-xs uppercase"><Plus size={16}/> 일정 추가</button>
             </div>
             <button onClick={handleSaveSchedule} className="w-full py-6 bg-pink-600 hover:bg-pink-700 text-white rounded-2xl font-black text-base tracking-wide shadow-lg shadow-pink-100 active:scale-95 transition-all flex items-center justify-center gap-2"><Save size={20}/> 일정 저장하기</button>
          </div>
        </div>
      )}

      {/* 모달: 공유 일정 상세 */}
      {showSharedScheduleModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-lg rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-xl font-black">{selectedSharedDate} 공유 일정</h3>
              <button onClick={() => setShowSharedScheduleModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={24}/></button>
            </div>
            <p className="text-[11px] text-slate-400 font-bold mb-6">저장하면 모든 직원에게 공유됩니다.</p>
            <div className="space-y-4 mb-8">
              {tempSharedItems.map((item, i) => (
                <div key={i} className="bg-slate-50/70 rounded-2xl p-3 space-y-2 border border-slate-100">
                  <div className="flex gap-2 items-center">
                    <select value={item.color} onChange={e => {
                      const next = [...tempSharedItems]; next[i] = {...next[i], color: e.target.value}; setTempSharedItems(next);
                    }} className="bg-white p-2 rounded-lg text-xs font-black border border-slate-100">
                      {SCHEDULE_COLORS.map(c => <option key={c.id} value={c.id}>{c.id}</option>)}
                    </select>
                    <input value={item.text} onChange={e => {
                      const next = [...tempSharedItems]; next[i] = {...next[i], text: e.target.value}; setTempSharedItems(next);
                    }} className="flex-1 bg-white p-3 rounded-xl font-bold text-sm outline-none border border-slate-100" />
                    <div className="flex flex-col items-end gap-0.5">
                      {item.authorName && <span className="text-[9px] text-slate-400 font-black whitespace-nowrap">{item.authorName}</span>}
                      {(item.userId === appUser?.id || isSystemAdmin) && (
                        <button onClick={() => {
                          if (item.rangeId && item.rangeStart && item.rangeEnd) {
                            if (!window.confirm('기간 일정입니다. 기간 전체를 삭제할까요?')) return;
                            setRemovedSharedRanges([...removedSharedRanges, { id: item.rangeId, start: item.rangeStart, end: item.rangeEnd }]);
                          }
                          setTempSharedItems(tempSharedItems.filter((_, idx) => idx !== i));
                        }} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={18}/></button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pl-1">
                    <span className="text-[11px] font-black text-slate-400">기간 종료일</span>
                    <input type="date" min={selectedSharedDate} value={item.endDate || item.rangeEnd || ''} onChange={e => {
                      const next = [...tempSharedItems]; next[i] = {...next[i], endDate: e.target.value}; setTempSharedItems(next);
                    }} className="bg-white p-2 rounded-lg text-xs font-bold border border-slate-100 outline-none" />
                    {(item.endDate || item.rangeEnd) && (
                      <>
                        <span className="text-[11px] font-bold text-pink-600">{selectedSharedDate} ~ {item.endDate || item.rangeEnd}</span>
                        <button onClick={() => {
                          const next = [...tempSharedItems]; next[i] = {...next[i], endDate: ''}; setTempSharedItems(next);
                        }} className="text-[11px] font-bold text-slate-400 underline">지우기</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <button onClick={() => setTempSharedItems([...tempSharedItems, { text: '', color: 'pink', authorName: appUser?.name, userId: appUser?.id }])} className="w-full py-3 border-2 border-dashed rounded-xl text-slate-400 hover:text-pink-600 hover:border-pink-200 transition-all flex items-center justify-center gap-2 font-black text-xs uppercase"><Plus size={16}/> 일정 추가</button>
            </div>
            <button onClick={handleSaveSharedSchedule} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all">전체 공유 저장</button>
          </div>
        </div>
      )}

      {/* 모달: 업무폰 교육현황 셀 편집 */}
      {showPhoneEduCellModal && selectedPhoneEduCell && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-sm rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-black text-base">{selectedPhoneEduCell.rowName}</h3>
              <button onClick={() => setShowPhoneEduCellModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={22}/></button>
            </div>
            <p className="text-[11px] text-slate-400 font-bold mb-5">{selectedPhoneEduCell.col} 항목 편집</p>

            <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">값 선택</p>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <button onClick={() => handlePhoneEduCellSave('O', '')} className="py-4 bg-emerald-50 text-emerald-600 rounded-2xl font-black text-2xl border-2 border-emerald-100 active:scale-95 transition-all hover:bg-emerald-100">O</button>
              <button onClick={() => handlePhoneEduCellSave('X', selectedPhoneEduCell.currentColor || '')} className="py-4 bg-red-50 text-red-500 rounded-2xl font-black text-2xl border-2 border-red-100 active:scale-95 transition-all hover:bg-red-100">X</button>
              <button onClick={() => handlePhoneEduCellSave('', '')} className="col-span-2 py-3.5 bg-slate-50 text-slate-400 rounded-2xl font-black text-sm border-2 border-slate-100 active:scale-95 transition-all hover:bg-slate-100">빈칸 (초기화)</button>
            </div>

            <div className="space-y-3 border-t border-slate-100 pt-5 mb-5">
              <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">날짜 직접 입력</p>
              <input
                value={phoneEduDateInput}
                onChange={e => setPhoneEduDateInput(e.target.value)}
                placeholder="예: 7.20~8.02"
                className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm outline-none border border-slate-100"
              />
              <button
                onClick={() => { if (phoneEduDateInput.trim()) handlePhoneEduCellSave(phoneEduDateInput.trim(), ''); }}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-sm active:scale-95 transition-all hover:bg-blue-700"
              >날짜로 저장</button>
            </div>

            <div className="border-t border-slate-100 pt-5">
              <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">셀 색상 (현재 값 유지)</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { c: '', label: '기본', bg: 'bg-white border-2 border-slate-200', text: 'text-slate-500' },
                  { c: 'yellow', label: '진행중', bg: 'bg-amber-100 border-2 border-amber-200', text: 'text-amber-700' },
                  { c: 'pink', label: '예정', bg: 'bg-pink-100 border-2 border-pink-200', text: 'text-pink-700' },
                ].map(opt => (
                  <button key={opt.c} onClick={() => handlePhoneEduCellColorOnly(opt.c)}
                    className={`py-3 rounded-2xl font-black text-[10px] active:scale-95 transition-all ${opt.bg} ${opt.text} ${selectedPhoneEduCell.currentColor === opt.c ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 모달: 열 추가 */}
      {showAddColModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-xs rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-black text-base">열 추가</h3>
              <button onClick={() => setShowAddColModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={22}/></button>
            </div>
            <input
              value={newColName}
              onChange={e => setNewColName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddPhoneEduCol()}
              placeholder="업무폰 이름 (예: 신규폰)"
              className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm outline-none border border-slate-100 mb-4"
            />
            <button onClick={handleAddPhoneEduCol} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-sm active:scale-95 transition-all">추가하기</button>
          </div>
        </div>
      )}

      {/* 모달: 사원 추가 */}
      {showAddEmployeeModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-xs rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-black text-base">사원 추가</h3>
              <button onClick={() => setShowAddEmployeeModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={22}/></button>
            </div>
            <input
              value={newEmployeeName}
              onChange={e => setNewEmployeeName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddPhoneEduEmployee()}
              placeholder="사원 이름"
              className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm outline-none border border-slate-100 mb-4"
            />
            <button onClick={handleAddPhoneEduEmployee} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-sm active:scale-95 transition-all">추가하기</button>
          </div>
        </div>
      )}

      {/* 모달: 보안교육 셀 편집 */}
      {showSecEduCellModal && selectedSecEduCell && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-xs rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-black text-base">{selectedSecEduCell.name}</h3>
              <button onClick={() => setShowSecEduCellModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={22}/></button>
            </div>
            <p className="text-[11px] text-slate-400 font-bold mb-6">
              {(() => { const [y,m] = selectedSecEduCell.monthKey.split('-'); return `${y}년 ${parseInt(m)}월 보안교육 날짜`; })()}
            </p>
            <input
              type="date"
              value={secEduDateInput}
              onChange={e => setSecEduDateInput(e.target.value)}
              className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm outline-none border border-slate-100 mb-4"
            />
            <div className="flex gap-3">
              <button onClick={() => handleSecEduCellSave('')} className="flex-1 py-4 bg-slate-50 text-slate-400 rounded-2xl font-black text-sm border border-slate-100 active:scale-95">삭제</button>
              <button onClick={() => { if (secEduDateInput) handleSecEduCellSave(secEduDateInput); }} className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-sm active:scale-95">저장</button>
            </div>
          </div>
        </div>
      )}

      {/* 모달: 보안교육 사원 추가 */}
      {showAddSecEduEmployeeModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-xs rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-black text-base">사원 추가</h3>
              <button onClick={() => setShowAddSecEduEmployeeModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={22}/></button>
            </div>
            <div className="space-y-3 mb-5">
              <select value={newSecEduEmployee.company} onChange={e => setNewSecEduEmployee(p => ({...p, company: e.target.value}))} className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm outline-none border border-slate-100 cursor-pointer">
                <option value="">소속회사 선택</option>
                {['더조은사람들','플라워일번지','채움플라워','해솔플라워','정플라워'].map(c => <option key={c} value={c}>{c}</option>)}
                <option value="직접입력">직접입력</option>
              </select>
              {newSecEduEmployee.company === '직접입력' && (
                <input value={newSecEduEmployee.companyCustom || ''} onChange={e => setNewSecEduEmployee(p => ({...p, companyCustom: e.target.value}))} placeholder="회사명 직접 입력" className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm outline-none border border-slate-100" />
              )}
              <input value={newSecEduEmployee.name} onChange={e => setNewSecEduEmployee(p => ({...p, name: e.target.value}))} onKeyDown={e => e.key === 'Enter' && handleAddSecEduEmployee()} placeholder="이름" className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm outline-none border border-slate-100" />
            </div>
            <button onClick={handleAddSecEduEmployee} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-sm active:scale-95">추가하기</button>
          </div>
        </div>
      )}

      {/* 모달: AI 분석 기준 보기·편집 */}
      {showGuidelineModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-lg rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-black flex items-center gap-2"><ClipboardList className="text-pink-500" size={20}/> AI 분석 기준</h3>
              <button onClick={() => setShowGuidelineModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={22}/></button>
            </div>
            <p className="text-[12px] text-slate-400 font-bold mb-5 leading-relaxed">회사 고유 용어와 업무 분류 규칙을 적어두면, AI가 요약할 때 이 기준을 최우선으로 따릅니다.</p>
            <textarea value={guidelineDraft} onChange={e => setGuidelineDraft(e.target.value)} placeholder={'예)\n- 취소창 정리, 고객주문창 정리, 합산입금 확인 → 고객주문 관리 업무 (정산 아님)\n- 지출결의서 작성, 부가세, 대금 재청구 → 정산·행정 업무\n- 카드결제 오류 대응, 상점아이디 확인 → 고객대응 업무'} className="w-full h-56 p-4 bg-slate-50 rounded-2xl font-bold text-[14px] outline-none border border-slate-100 focus:bg-white focus:border-pink-300 transition-all resize-none leading-relaxed" />
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowGuidelineModal(false)} className="flex-1 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black text-sm active:scale-95 transition-all">취소</button>
              <button onClick={saveAiGuideline} className="flex-[2] py-4 bg-pink-600 hover:bg-pink-700 text-white rounded-2xl font-black text-sm shadow-lg shadow-pink-100 active:scale-95 transition-all">저장</button>
            </div>
          </div>
        </div>
      )}

      {/* 모달: AI 요약 실행 전 안내 */}
      {showAiConfirm && (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-md rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans">
            <div className="flex items-center gap-2 mb-4">
              <Flower className="text-pink-500" size={22}/>
              <h3 className="text-lg font-black">AI 요약 안내</h3>
            </div>
            <p className="text-[16px] text-red-500 font-normal mb-6 text-center">실행 전에 아래 내용을 확인해 주세요!</p>
            <div className="space-y-3 mb-7">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl p-3.5">
                <span className="text-lg leading-none">💰</span>
                <p className="text-[13px] font-bold text-amber-800 leading-relaxed">AI 요약은 <b className="font-black">1회당 토큰이 과금</b>됩니다. 필요할 때만 사용해 주세요.</p>
              </div>
              <div className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-2xl p-3.5">
                <span className="text-lg leading-none">🤖</span>
                <p className="text-[13px] font-bold text-slate-600 leading-relaxed">사용 모델: <b className="font-black text-slate-800">Claude Opus 4.8</b></p>
              </div>
              <div className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-2xl p-3.5">
                <span className="text-lg leading-none">📅</span>
                <p className="text-[13px] font-bold text-slate-600 leading-relaxed">정확한 분석을 위해 <b className="font-black text-slate-800">최소 1개월 이상 기간</b>을 선택한 뒤 요약하는 것을 권장합니다.</p>
              </div>
              <div className="flex items-start gap-3 bg-pink-50 border border-pink-100 rounded-2xl p-3.5">
                <span className="text-lg leading-none">⚠️</span>
                <p className="text-[13px] font-bold text-pink-800 leading-relaxed">AI 요약은 <b className="font-black">참고용</b>이며, 실제 평가·판단은 <b className="font-black">평가자 본인</b>에게 있습니다.</p>
              </div>
            </div>
            <div className="mb-4 text-center">
              <div className="text-[15px] font-black text-slate-700">현재 선택</div>
              <div className="text-[15px] font-bold text-slate-600 mt-1">{filterMember === 'all' ? '전 직원 (회사 전체 요약)' : (profiles[filterMember]?.name || filterMember)} · {appliedStartDate} ~ {appliedEndDate}</div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowAiConfirm(false)} className="flex-1 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black text-sm active:scale-95 transition-all">취소</button>
              <button onClick={() => { setShowAiConfirm(false); handleAiSummary(); }} className="flex-[2] py-4 bg-pink-600 hover:bg-pink-700 text-white rounded-2xl font-black text-sm shadow-lg shadow-pink-100 active:scale-95 transition-all">동의하고 요약 시작</button>
            </div>
          </div>
        </div>
      )}

      {/* 모달: AI 업무일지 요약 (최고관리자 전용) */}
      {showAiSummaryModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-2xl rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans max-h-[88vh] flex flex-col">
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-lg font-black flex items-center gap-2"><Flower className="text-pink-500" size={20}/> AI 업무일지 요약</h3>
              <button onClick={() => setShowAiSummaryModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={22}/></button>
            </div>
            <p className="text-[11px] text-slate-400 font-bold mb-5">{aiSummaryMeta.startDate} ~ {aiSummaryMeta.endDate} · 최고관리자 전용</p>
            <div className="flex-1 overflow-y-auto no-scrollbar">
              {aiSummaryLoading && (
                <div className="py-20 text-center">
                  <Flower className="text-pink-500 w-10 h-10 mx-auto animate-bounce mb-4" />
                  <p className="font-black text-slate-400 text-sm">AI가 업무일지를 분석하고 있습니다...</p>
                  <p className="font-bold text-slate-300 text-xs mt-1">잠시만 기다려 주세요 (최대 1분)</p>
                </div>
              )}
              {aiSummaryError && !aiSummaryLoading && (
                <div className="py-16 text-center px-6">
                  <AlertTriangle className="text-rose-400 w-9 h-9 mx-auto mb-3" />
                  <p className="font-black text-slate-600 text-sm">요약을 불러오지 못했습니다</p>
                  <p className="font-bold text-slate-400 text-xs mt-2 break-words">{aiSummaryError}</p>
                </div>
              )}
              {aiSummaryResult && !aiSummaryLoading && (
                <div>
                  <div className="flex items-center justify-between gap-3 mb-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center text-white font-black">{aiSummaryResult.userName?.charAt(0)}</div>
                      <div>
                        <div className="font-black text-slate-800">{aiSummaryResult.userName}</div>
                        <div className="text-[11px] font-bold text-slate-400">업무일지 {aiSummaryResult.count}건 분석</div>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={wordAiSummary} className="flex items-center gap-1 px-3 py-2 bg-white text-blue-600 hover:bg-blue-50 rounded-xl font-black text-[11px] border border-blue-200 active:scale-95 transition-all"><Save size={13}/> Word</button>
                      <button onClick={pdfAiSummary} className="flex items-center gap-1 px-3 py-2 bg-white text-rose-600 hover:bg-rose-50 rounded-xl font-black text-[11px] border border-rose-200 active:scale-95 transition-all"><Save size={13}/> PDF</button>
                      <button onClick={printAiSummary} className="flex items-center gap-1 px-3 py-2 bg-pink-600 text-white rounded-xl font-black text-[11px] active:scale-95 transition-all"><Printer size={13}/> 인쇄</button>
                    </div>
                  </div>
                  <div className="text-[14px] leading-relaxed font-bold text-slate-700 bg-slate-50/50 p-5 rounded-2xl border border-slate-50 space-y-1">
                    {(() => {
                      const out = []; let sec = '';
                      cleanSummary(aiSummaryResult.summary).split('\n').forEach((line, i) => {
                        const t = line.trim();
                        if (!t) { out.push(<div key={i} className="h-1.5"></div>); return; }
                        if (t.startsWith('【')) { sec = t; out.push(<div key={i} className="font-black text-pink-600 pt-2 first:pt-0 text-[13px]">{t}</div>); return; }
                        if (sec.includes('주요 업무 영역')) {
                          const m = t.match(/^([-·•]\s*)?([^:：(]{1,20})[:：]\s*(.+)$/);
                          if (m) { out.push(<div key={i} className="whitespace-pre-wrap">{m[1] ? '· ' : ''}<span className="font-black text-slate-800">{m[2].trim()}</span> : {m[3]}</div>); return; }
                        }
                        if (sec.includes('주요 성과') && !/^[-·•]/.test(t) && !/[:：]/.test(t) && t.length <= 16 && !/[.。!?…]$/.test(t)) {
                          out.push(<div key={i} className="font-black text-slate-800 pt-1.5 whitespace-pre-wrap">{t}</div>); return;
                        }
                        out.push(<div key={i} className="whitespace-pre-wrap">{line}</div>);
                      });
                      return out;
                    })()}
                  </div>
                  <p className="text-[10px] text-slate-300 font-bold mt-3 text-center">※ AI가 생성한 요약입니다. 원문과 대조하여 확인하세요.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 모달: 회의록 AI 정리 미리보기 */}
      {showMeetingAiModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-2xl rounded-[32px] p-8 shadow-2xl animate-in zoom-in-95 font-sans max-h-[88vh] flex flex-col">
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-lg font-black flex items-center gap-2"><Flower className="text-pink-500" size={20}/> AI 회의록 정리</h3>
              <button onClick={() => setShowMeetingAiModal(false)} className="p-2 hover:bg-slate-50 rounded-full"><X size={22}/></button>
            </div>
            <p className="text-[11px] text-slate-400 font-bold mb-5">회사 표준 양식으로 정리한 미리보기입니다. 적용 후 수정할 수 있습니다.</p>
            <div className="flex-1 overflow-y-auto no-scrollbar">
              {meetingAiLoading && (
                <div className="py-20 text-center">
                  <Flower className="text-pink-500 w-10 h-10 mx-auto animate-bounce mb-4" />
                  <p className="font-black text-slate-400 text-sm">AI가 회의 내용을 정리하고 있습니다...</p>
                  <p className="font-bold text-slate-300 text-xs mt-1">잠시만 기다려 주세요 (최대 1분)</p>
                </div>
              )}
              {meetingAiError && !meetingAiLoading && (
                <div className="py-16 text-center px-6">
                  <AlertTriangle className="text-rose-400 w-9 h-9 mx-auto mb-3" />
                  <p className="font-black text-slate-600 text-sm">정리하지 못했습니다</p>
                  <p className="font-bold text-slate-400 text-xs mt-2 break-words">{meetingAiError}</p>
                </div>
              )}
              {meetingAiResult && !meetingAiLoading && (
                <div className="text-[14px] leading-relaxed font-bold text-slate-700 bg-slate-50/50 p-5 rounded-2xl border border-slate-50">
                  {renderMeetingBody(meetingAiResult)}
                </div>
              )}
            </div>
            {meetingAiResult && !meetingAiLoading && (
              <div className="flex gap-3 pt-5 font-black">
                <button onClick={() => setShowMeetingAiModal(false)} className="flex-1 py-4 bg-slate-100 rounded-2xl text-xs uppercase active:scale-95">닫기</button>
                <button onClick={applyMeetingOrganize} className="flex-[2] py-4 bg-pink-600 text-white rounded-2xl text-xs uppercase tracking-widest shadow-xl active:scale-95">이 내용으로 적용</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 모달: 비밀번호 변경 */}
      {showMyProfileModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm print:hidden">
          <div className="bg-white w-full max-w-[340px] rounded-[32px] p-7 shadow-2xl font-sans animate-in zoom-in-95">
            <div className="flex justify-between items-center mb-6">
               <h3 className="text-base font-black flex items-center gap-2"><KeyRound className="text-pink-600" size={18}/> 비밀번호 변경</h3>
               <button onClick={() => setShowMyProfileModal(false)} className="p-2 hover:bg-slate-50 rounded-full transition-colors"><X size={22}/></button>
            </div>
            <div className="space-y-4 mb-8">
               <div className="bg-slate-50 p-4 rounded-2xl border">
                  <span className="text-[10px] text-slate-400 uppercase font-black block mb-1 tracking-wider">아이디</span>
                  <span className="font-bold text-slate-700 text-sm">{appUser?.id}</span>
               </div>
               <div className="space-y-2">
                  <span className="text-[10px] text-pink-600 uppercase font-black px-1 tracking-wider">새 비밀번호</span>
                  <input type="password" value={myNewPw} onChange={e => setMyNewPw(e.target.value)} placeholder="새 비밀번호 입력" className="w-full p-4 bg-slate-50 rounded-2xl border-none outline-none shadow-inner font-bold text-sm" />
               </div>
            </div>
            <button 
              onClick={async () => {
                if(!myNewPw.trim()) return showMsg('비밀번호를 입력하세요.', 'error');
                try {
                  await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'user_profiles', appUser.id), { pw: myNewPw });
                  showMsg('비밀번호가 성공적으로 변경되었습니다.');
                  setAppUser({ ...appUser, pw: myNewPw });
                  setShowMyProfileModal(false);
                } catch(e) { showMsg('변경 실패', 'error'); }
              }} 
              className="w-full py-4.5 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all"
            >
              저장하기
            </button>
          </div>
        </div>
      )}

      {/* 모달: 시스템 관리 */}
      {showUserManageModal && isSystemAdmin && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-2 md:p-4 bg-black/60 backdrop-blur-sm print:hidden">
              <div className="bg-white w-full max-w-4xl rounded-[32px] md:rounded-[40px] p-6 md:p-10 shadow-2xl flex flex-col max-h-[95vh] font-sans border border-slate-100">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-black flex items-center gap-3 uppercase tracking-tighter"><UserCog className="text-pink-600" size={26}/> 시스템 관리 센터</h3>
                      <button onClick={() => {setShowUserManageModal(false); setShowAddUserForm(false);}} className="p-2 hover:bg-slate-50 rounded-full transition-colors"><X size={26}/></button>
                  </div>
                  
                  <div className="mb-6">
                    <button 
                      onClick={() => setShowAddUserForm(!showAddUserForm)} 
                      className={`w-full py-4 rounded-2xl font-black flex items-center justify-center gap-3 transition-all ${showAddUserForm ? 'bg-slate-100 text-slate-600' : 'bg-pink-600 text-white shadow-lg'}`}
                    >
                      {showAddUserForm ? <X size={20}/> : <UserPlus size={20}/>}
                      {showAddUserForm ? '닫기' : '새 직원 등록'}
                    </button>
                  </div>

                  {showAddUserForm && (
                    <div className="mb-8 p-6 bg-slate-50 rounded-[32px] border-2 border-pink-100 animate-in zoom-in-95 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input placeholder="아이디" value={newUserData.id} onChange={e => setNewUserData({...newUserData, id: e.target.value})} className="p-3.5 bg-white rounded-xl border-none outline-none shadow-sm font-bold text-sm" />
                        <input placeholder="이름" value={newUserData.name} onChange={e => setNewUserData({...newUserData, name: e.target.value})} className="p-3.5 bg-white rounded-xl border-none outline-none shadow-sm font-bold text-sm" />
                        <input type="password" placeholder="초기 비밀번호" value={newUserData.pw} onChange={e => setNewUserData({...newUserData, pw: e.target.value})} className="p-3.5 bg-white rounded-xl border-none outline-none shadow-sm font-bold text-sm" />
                        <div className="grid grid-cols-2 gap-2">
                          <select value={newUserData.role} onChange={e => setNewUserData({...newUserData, role: e.target.value})} className="p-3.5 bg-white rounded-xl border-none outline-none shadow-sm font-bold text-sm cursor-pointer">
                            {ranks.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                          <select value={newUserData.dept} onChange={e => setNewUserData({...newUserData, dept: e.target.value})} className="p-3.5 bg-white rounded-xl border-none outline-none shadow-sm font-bold text-sm cursor-pointer">
                            {depts.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                      </div>
                      <button 
                        onClick={async () => {
                          if (!newUserData.id || !newUserData.name || !newUserData.pw) return showMsg('모든 필드를 입력하세요.', 'error');
                          try {
                            await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'user_profiles', newUserData.id), {
                              name: newUserData.name, pw: newUserData.pw, role: newUserData.role, dept: newUserData.dept,
                              approved: true, visible: true, createdAt: serverTimestamp()
                            });
                            showMsg('새 직원이 추가되었습니다.');
                            setNewUserData({ id: '', name: '', pw: '', role: '선임', dept: '본사' });
                            setShowAddUserForm(false);
                          } catch (err) { showMsg('추가 실패', 'error'); }
                        }} 
                        className="w-full py-4 bg-slate-900 text-white rounded-xl font-black uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all"
                      >
                        등록 완료
                      </button>
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto pr-1 no-scrollbar space-y-4">
                      {Object.entries(profiles).sort((a,b) => (RANK_PRIORITY[a[1].role] ?? 99) - (RANK_PRIORITY[b[1].role] ?? 99)).map(([id, u]) => (
                          <div key={id} className="p-4 md:p-6 bg-slate-50 rounded-[28px] border border-white shadow-sm flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-slate-300 font-black text-lg shadow-sm">{u.name?.charAt(0)}</div>
                                  <div>
                                      <span className="font-black block text-slate-900 text-sm md:text-base">{u.name}</span>
                                      <span className="text-[10px] text-pink-500 font-black uppercase tracking-widest">{u.role} | {u.dept || '본사'}</span>
                                  </div>
                              </div>
                              <div className="flex gap-1.5">
                                  <button onClick={() => { if(window.confirm('비밀번호를 0000으로 초기화하시겠습니까?')) updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'user_profiles', id), { pw: '0000' }); }} className="p-2.5 bg-white text-slate-300 hover:text-pink-600 rounded-xl transition-all border border-slate-50"><KeyRound size={18}/></button>
                                  <button onClick={() => { if(window.confirm('해당 사용자를 완전히 삭제하시겠습니까?')) deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'user_profiles', id)); }} className="p-2.5 bg-white text-slate-300 hover:text-red-500 rounded-xl transition-all border border-slate-50"><Trash2 size={18}/></button>
                              </div>
                          </div>
                      ))}
                  </div>
              </div>
          </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Pretendard:wght@400;500;600;700;900&display=swap');
        body { font-family: 'Pretendard', sans-serif; letter-spacing: -0.04em; background-color: #F8FAFC; -webkit-font-smoothing: antialiased; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        
        /* 인쇄 최적화 스타일 */
        @media print { 
            @page { margin: 15mm; }
            nav, .md\\:hidden, button, select, input, .absolute, form, .print\\:hidden { display: none !important; } 
            main { padding: 0 !important; width: 100% !important; max-width: 100% !important; margin: 0 !important; } 
            .bg-white { border: none !important; box-shadow: none !important; background: transparent !important; } 
            body { padding: 0 !important; background: white !important; margin: 0 !important; color: #000 !important; }
            
            /* 일지 항목 잘림 방지 및 스타일 */
            .log-card { 
                page-break-inside: avoid !important; 
                break-inside: avoid !important; 
                margin-bottom: 20px !important; 
                border: 1px solid #e2e8f0 !important; 
                border-radius: 12px !important;
                padding: 1.5rem !important;
                box-shadow: none !important;
            }
            .log-card span, .log-card div { color: #000 !important; }
            
            /* 회의록 등 긴 항목 잘림 방지 */
            .page-break-inside-avoid {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
            }
            
            /* 불필요한 공백 제거 */
            .mx-auto { margin-left: 0 !important; margin-right: 0 !important; }
            .shadow-2xl, .shadow-md { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}